import { Component, OnInit, ChangeDetectorRef, NgZone, inject, ElementRef, ViewChild, AfterViewInit, OnDestroy, ViewChildren, QueryList, ChangeDetectionStrategy, OnChanges, SimpleChanges, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { LetDirective } from '@ngrx/component';
import { Observable, of, shareReplay, Subscription, take, forkJoin, catchError, map, fromEvent, timeout, retry, combineLatest, switchMap, timer } from 'rxjs';
import { debounceTime, throttleTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { DashboardService } from '../../../services/dashboard/dashboard.service';
import { CompactType, DisplayGrid, GridType, Gridster, GridsterConfig, GridsterItem, GridsterItemConfig } from 'angular-gridster2';
import { AddDashboardItemDialogComponent } from './add-dashboard-item-dialog/add-dashboard-item-dialog.component';
import { DashboardTileModel } from '../../../models/dashboard/DashboardTile.model';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environment/environment';
import { AuthService } from '../../../services/auth/auth.service';
import { DunaDashboardAdapter } from './duna-dashboard-adapter';
import { DashboardLayoutService } from '../../../services/dashboard/dashboard-layout.service';
import { ChartService } from '../../../services/dashboard/chart.service';
import { WidgetAdapterService } from '../../../services/dashboard/widget-adapter.service';
import { DashboardModule } from '../../../services/dashboard/dashboard.module';
import { LocationSelectionDialogComponent } from './location-selection-dialog/location-selection-dialog.component';
import { LocationMultiSelectionDialogComponent } from './location-multi-selection-dialog/location-multi-selection-dialog.component';
import { EducationSelectionDialogComponent } from './education-selection-dialog/education-selection-dialog.component';
import { ConfirmResetDialogComponent } from './confirm-reset-dialog.component';
import { DunaValidationIndicatorComponent, DunaValidationStatus } from '../duna-validation-indicator/duna-validation-indicator.component';
import { Store } from '@ngrx/store';
import { selectWidgetData, selectWidgetDataLoading, selectWidgetDataError } from '../../../ngrx/dashboard/dashboard.selectors';
import * as DashboardActions from '../../../ngrx/dashboard/dashboard.actions';
import { selectUserWidgetConfig, selectCurrentUserId } from '../../../ngrx/dashboard/dashboard.selectors';
import { selectUserRoles, selectCurrentPortal, selectIsAuthInitialized } from '../../../ngrx/auth/auth.selectors';
import { DashboardConfigDTO, DashboardWidgetConfigDTO } from '../../../dto/dashboard/dashboard.dto';
// Import the getUserProgressData and getEducationalPaths functions
import { getUserProgressData, getEducationalPaths } from '../../../services/dashboard/data/user-progress.data';
import { extractLocationNamesFromListFilters } from '../../../services/dashboard/user-progress-dashboard.shared';
// Import the new getLocationProgressData function
import { getLocationProgressData, forceReloadLocationProgressData } from '../../../services/dashboard/data/location-progress.data';
// Import the simple number data function
import { 
  getSimpleNumberData, 
  getUserCountData, 
  getCompletionRateData, 
  getCompletedUsersCount, 
  getUsersWithWarningsCount,
  getRegisteredToProgressRatio
} from '../../../services/dashboard/data/simple-number.data';
import { DashboardTemplateManagerComponent } from './dashboard-template-manager.component';
import { DashboardShareLinkContext } from '../../../services/dashboard/dashboard-share-link.context';
// Import ListRequestDTO and ListResponseDTO
import { ListRequestDTO, createListRequest } from '../../../dto/list-request.dto';
import { ListResponseDTO } from '../../../dto/list-response.dto';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Router, RouterLink } from '@angular/router';
import {
  getCateringSummaryData,
  CateringSummaryEvent,
  normalizeCateringSummaryDays,
  CATERING_SUMMARY_DEFAULT_DAYS,
} from '../../../services/dashboard/data/catering-summary.data';
import { GroupSelectionDialogComponent } from './group-selection-dialog/group-selection-dialog.component';
import { DunaCateringPreferencesDialogComponent, DUNA_CATERING_PREFERENCES_DIALOG_CONFIG } from '../duna-catering/duna-catering-preferences-dialog.component';
import { EventCateringApprovalListItemDTO } from '../../../dto/event/event-catering-approval-list-item.dto';
import { DunaCateringSummaryWidgetComponent } from './catering-summary-widget/duna-catering-summary-widget.component';
import { DunaDashboardEventsCalendarWidgetComponent } from './duna-dashboard-events-calendar-widget/duna-dashboard-events-calendar-widget.component';
import { DashboardEventsCalendarFilterPanelComponent } from './dashboard-events-calendar-filter-panel/dashboard-events-calendar-filter-panel.component';
import { DunaUserProgressLabelComponent } from '../duna-user-progress-label/duna-user-progress-label.component';
import { CateringGroupFilterOption, loadCateringGroupFilterOptions } from '../../../services/component-data-source/data/event/duna_catering_list.shared';

// Import WidgetDefinition from the adapter service 
// or define it here to match the implementation
export interface WidgetDefinition {
  type: string;
  title: string;
  description?: string;
  icon?: string;
  defaultSize?: { cols: number; rows: number };
  defaultDisplayType?: string;
  defaultFields?: string[];
  visible?: boolean; // Add visibility control for the add widget dialog
}

// Interface definition to fix widget config service type errors
interface WidgetRegistry {
  getWidgetConfig(type: string): any;
  getAllWidgets(): any[];
  getData(dataSourceId: string, filters?: any): Observable<any>;
  getDataSource(dataSourceId: string): any;
}

// New interfaces for the modern dashboard
export interface WidgetLayout {
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
}

export interface WidgetSettings {
  category: string;
  filters: Record<string, any>;
  fields: string[];
  displayType: string;
  // Table settings
  pageable?: boolean;
  pageSize?: number;
  sortable?: boolean;
  searchable?: boolean;
  rowHover?: boolean;
  filterable?: boolean;
  striped?: boolean;
  dense?: boolean;
}

export interface WidgetData {
  id: string;
  type: string;
  title: string;
  layout?: WidgetLayout;
  position?: GridsterItemConfig;
  x?: number;
  y?: number;
  cols?: number;
  rows?: number;
  data?: any;
  dataSource?: string;
  settings?: WidgetSettings;
  retryCount?: number;
  defaultFilters?: Record<string, any>;
  isLoading?: boolean;
  lastRetryTime?: number;
  originalData?: any; // Backup of original data to preserve filter options
  // Pagination state for infinite scrolling
  currentPage?: number;
  totalPages?: number;
  isLoadingMore?: boolean;
  hasMoreData?: boolean;
  eventsCalendarReloadToken?: number;
  /** False until first list fetch completes (user/location progress). */
  initialLoadComplete?: boolean;
}

export type WidgetAction = 
  | { type: 'add'; widgetType: string }
  | { type: 'remove'; id: string }
  | { type: 'edit'; id: string }
  | { type: 'updateLayout'; id: string; layout: WidgetLayout }
  | { type: 'updateData'; id: string; data: any }
  | { type: 'updateSettings'; id: string; settings: WidgetSettings };

interface TableSettings {
  columns: {
    key: string;
    header: string;
    type: 'text' | 'date' | 'boolean' | 'button' | 'select';
    sortable?: boolean;
    visible?: boolean;
  }[];
  sort?: {
    column: string;
    direction: 'asc' | 'desc';
  };
  pageable?: boolean;
  pageSize?: number;
  sortable?: boolean;
  filterable?: boolean;
  rowHover?: boolean;
  striped?: boolean;
  dense?: boolean;
  searchable?: boolean;
}

// Define metric type for year comparison
export interface YearComparisonMetric {
  name: string;
  thisYear: number;
  lastYear: number;
  percentChange: number;
}

// Define comparison item type for year comparison
export interface YearComparisonItem {
  label: string;
  currentYear: number;
  previousYear: number;
}

export type DunaDashboardViewMode = 'default' | 'publicShare' | 'shareLinkAdminFilters';

@Component({
  selector: 'app-duna-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    Gridster,
    GridsterItem,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    DashboardModule,
    DunaValidationIndicatorComponent,
    LetDirective,
    OverlayModule,
    RouterLink,
    DunaCateringSummaryWidgetComponent,
    DunaDashboardEventsCalendarWidgetComponent,
    DashboardEventsCalendarFilterPanelComponent,
    DunaUserProgressLabelComponent,
  ],
  providers: [],
  templateUrl: './duna-dashboard.component.html',
  styleUrls: ['./duna-dashboard.component.optimized.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DunaDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() shareLinkGuid: string | null = null;
  @Input() viewMode: DunaDashboardViewMode = 'default';

  private shareLinkContext = inject(DashboardShareLinkContext);
  private _editMode = false;
  
  get editMode(): boolean {
    return this._editMode;
  }
  
  set editMode(value: boolean) {
    this._editMode = value;
    // When edit mode changes, ensure gridster options are updated
    if (this.options) {
      this.syncGridsterOptionsWithEditMode();
    }
  }
  
  options!: GridsterConfig;
  dashboard: WidgetData[] = [];
  private dataSourceCache = new Map<string, Observable<any>>();
  private resizeTimeout: any;
  pastLayouts: GridsterItemConfig[][] = [];
  futureLayouts: GridsterItemConfig[][] = [];
  isDialogOpen = false;
  selectedWidget: WidgetData | null = null;
  isSettingsOpen = false;
  isFilterOpen = false;
  isLayoutSelectorOpen = false;
  availableLayouts: {id: string, name: string, description: string, widgets?: any[]}[] = [];
  
  // Add properties for initialization tracking
  initializing = true;
  loadDashboardComplete = false;
  private dashboardLoadSeq = 0;
  private dashboardServerFetchInFlight = false;
  loadedDashboard: DashboardConfigDTO | null = null;
  loadedDashboardCanEditTemplate = false;
  initialLayoutSaved = false;
  showEmptyState = false;
  
  // Track scroll loading state per widget to prevent duplicate requests
  private scrollLoadingMap = new Map<string, boolean>();
  private scrollThrottleMap = new Map<string, number>();
  private static readonly PROGRESS_LIST_LOAD_MORE_UI_DELAY_MS = 2000;
  private progressListLoadMoreUiVisible = new Map<string, boolean>();
  private progressListLoadMoreUiTimers = new Map<string, ReturnType<typeof setTimeout>>();
  // Track widgets that are currently restoring scroll position to prevent scroll events
  private scrollRestoringMap = new Map<string, boolean>();
  
  // Add static initialization and stability tracking
  private static hasInitialized = false;
  private static initializationInProgress = false;  // Add static flag for initialization in progress
  private static dashboardData: WidgetData[] = []; // Static storage for dashboard data
  private static widgetDataLoaded = false; // Track if widget data has been loaded
  
  // Make these values much higher for a more aggressive breaking approach
  static lastDestroyTime = 0;
  static recreationCount = 0;
  static readonly RECREATION_COOLDOWN = 10000; // 10 seconds cooldown
  static readonly MAX_RECREATIONS = 1; // Lower this to break the cycle sooner (changed from 2 to 1)
  static breakingLoop = false; // Add breaking mechanism
  static forceStableView = false; // Add flag to completely force stable view
  static forceWait = false; // Add a new flag to force a waiting period
  static forceSingleInitialization = false; // New flag to ensure one-time initialization
  
  // Add storage for the last user session
  private static lastLoggedInUser: string | null = null;
  private static stableInitComplete = false; // Track completion of stable initialization
  
  // Add static accessors for template usage
  get DunaDashboardComponent() {
    return DunaDashboardComponent;
  }
  
  get widgetTitles(): Record<string, string> {
    // Get all available widgets and extract their titles
    return this.availableWidgetDefinitions.reduce((titles: Record<string, string>, widget: WidgetDefinition) => {
      titles[widget.type] = widget.title;
      return titles;
    }, {} as Record<string, string>);
  }
  activeSettingsTab = 'general';
  
  // Available widget types based on environment
  availableWidgetTypes: string[] = [];
  availableWidgetDefinitions: WidgetDefinition[] = [];
  
  // Chart management - now using ChartService
  private resizeObserver: ResizeObserver | null = null;
  private gridLayoutObserver: ResizeObserver | null = null;
  private gridLayoutTimer: ReturnType<typeof setTimeout> | null = null;
  private gridsterStubApi: GridsterConfig['api'] | null = null;
  private static readonly GRID_LAYOUT_MAX_ATTEMPTS = 60;
  private lastLaidOutWidth = 0;
  private lastLaidOutWidgetCount = -1;
  private gridLayoutAttempts = 0;
  private intersectionObserver: IntersectionObserver | null = null;
  private widgetsInView: Set<string> = new Set();
  
  // Add our adapter
  private dashboardAdapter = inject(DunaDashboardAdapter);
  private chartService = inject(ChartService);
  private subscription = new Subscription();

  // Add ViewChildren references
  @ViewChildren('chartCanvas') chartCanvases!: QueryList<ElementRef>;
  @ViewChildren('statusChartCanvas') statusChartCanvases!: QueryList<ElementRef>;
  @ViewChild('filterPanelTemplate') filterPanelTemplate!: TemplateRef<unknown>;
  @ViewChild('settingsDialogTemplate') settingsDialogTemplate!: TemplateRef<unknown>;
  @ViewChild('dashboardOuter') dashboardOuter?: ElementRef<HTMLElement>;

  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private router = inject(Router);
  private filterOverlayRef: OverlayRef | null = null;
  private settingsOverlayRef: OverlayRef | null = null;

  // Widget selection for add dialog
  selectedWidgetType = '';

  // Track chart initialization state
  private initializedCharts = new Set<string>();
  private chartInitializationInProgress = new Set<string>();  // Add instance property for chart initialization
  private dashboardInitialized = false;
  isDestroyed = false;
  private static chartInstances = new Map<string, boolean>();
  // Track destroyed charts
  private destroyedCharts = new Set<string>();

  // Add a class property to track the current viewport mode
  public isDesktopMode = true;
  private resizeListener: any;
  private targetBreakpoint = 1320; // Target viewport width breakpoint
  public innerWidth = window.innerWidth; // Add public property for width

  // Add missing service properties
  private dashboardDataRegistry: WidgetRegistry;

  // Save dashboard to service and localStorage
  private savePending = false;
  public isSaving = false;
  private saveTimeout: any = null;

  // Add method to handle item resize
  private resizeTimeouts: Map<string, any> = new Map();

  // Flag to hide debug elements in production
  isProduction = environment.production;

  // Location filtering properties
  locationSearchText = '';
  filteredLocations: string[] = [];

  // Cached locations and educational paths for filter dialogs (per widget id)
  private allWidgetLocations = new Map<string, string[]>();
  private allWidgetLocationsLoading = new Map<string, boolean>();
  educationalPaths: {id: string | number, name: string, full_name?: string}[] = [];
  private educationalPathsLoaded = false;
  userGroups: {id: number, name: string}[] = [];
  groupsEnabled = false;
  private userGroupsLoaded = false;
  cateringGroupFilterOptions: CateringGroupFilterOption[] = [];

  // Add current user ID property
  /** Calendar year for progress labels in dashboard widgets. */
  readonly currentYear = new Date().getFullYear();
  public currentUserId: string | null = null;

  private readonly dashboardEditRoles = ['orgbruger', 'orgadmin', 'superadmin'];

  // Observable to check if user can edit dashboard (only orgbruger, orgadmin, superadmin)
  canEditDashboard$ = combineLatest([
    this.store.select(selectUserRoles),
    this.store.select(selectCurrentPortal),
    this.authService.getRoles()
  ]).pipe(
    map(([storeRoles, portal, authRoles]) => {
      const roles = (storeRoles.length > 0 ? storeRoles : authRoles).map((role: string) => role.toLowerCase());
      const hasEditRole = this.dashboardEditRoles.some(role => roles.includes(role));
      return hasEditRole || portal === 'administration';
    }),
    shareReplay(1)
  );

  isSuperadmin$ = combineLatest([
    this.store.select(selectUserRoles),
    this.authService.getRoles()
  ]).pipe(
    map(([storeRoles, authRoles]) => {
      const roles = (storeRoles.length > 0 ? storeRoles : authRoles).map((role: string) => role.toLowerCase());
      return roles.includes('superadmin');
    }),
    shareReplay(1)
  );

  get showDashboardChrome(): boolean {
    return this.viewMode === 'default' || this.viewMode === 'shareLinkAdminFilters';
  }

  get isPublicShareView(): boolean {
    return this.viewMode === 'publicShare';
  }

  get isShareLinkAdminFiltersView(): boolean {
    return this.viewMode === 'shareLinkAdminFilters';
  }

  canOpenWidgetFilters(): boolean {
    return this.viewMode !== 'publicShare';
  }

  savingShareLinkFilters = false;

  // Export-related properties
  public selectedExportFormat: 'excel' | 'pdf' | 'csv' = 'excel';
  public exportIncludeHeaders: boolean = true;
  public exportIncludeFilters: boolean = false;
  public exportIncludeLogo: boolean = true;
  public customExportFilename: string = '';
  public isExporting: boolean = false;

  // Make Math available in template
  public Math = Math;

  // Helper function to format percentages like brugere_status.ts
  public formatDualPercentage(intendedPercentage: number, participatedPercentage: number): string {
    const intended = Math.round(Math.min(100, Math.max(0, intendedPercentage || 0)));
    const participated = Math.round(Math.min(100, Math.max(0, participatedPercentage || 0)));
    return `${intended}% (${participated}%)`;
  }

  // Helper function to clamp percentage values between 0-100
  public clampPercentage(value: number): number {
    return Math.min(100, Math.max(0, value || 0));
  }

  constructor(
    public dashboardService: DashboardService,
    private widgetAdapter: WidgetAdapterService,
    private layoutService: DashboardLayoutService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private http: HttpClient,
    private componentDataSourceService: ComponentDataSourceService,
    private authService: AuthService,
    private elementRef: ElementRef,
    private store: Store,
    private snackBar: MatSnackBar
  ) {
    
    // Initialize mock registry
    this.dashboardDataRegistry = {
      getWidgetConfig: (type: string) => {
        const config = this.widgetAdapter.getWidgetConfig(type);
        return {
          type: config.type,
          title: config.title,
          defaultSize: config.defaultSize,
          dataSource: type
        };
      },
      getAllWidgets: () => {
        return this.widgetAdapter.getAllWidgetTypes().map(type => {
          const config = this.widgetAdapter.getWidgetConfig(type);
          return {
            id: type,
            typeValue: type, // Renamed to avoid duplicate key
            title: config.title,
            defaultSize: config.defaultSize,
            dataSource: type
          };
        });
      },
      getData: (dataSourceId: string, filters?: any) => {
        return of({ items: [] });
      },
      getDataSource: (dataSourceId: string) => {
        return {
          id: dataSourceId,
          mockData: { items: [] }
        };
      }
    };
    
    // Get available widget types
    this.availableWidgetDefinitions = this.widgetAdapter.getAllWidgetTypes().map(type => {
      const config = this.widgetAdapter.getWidgetConfig(type);
      return {
        type,
        title: config.title,
        description: config.description,
        icon: config.icon,
        defaultSize: config.defaultSize,
        defaultDisplayType: config.defaultDisplayType,
        defaultFields: config.defaultFields,
        visible: type === 'userProgress' || type === 'userProgressStats' || type === 'locationProgress' || type === 'simpleNumber' || type === 'cateringSummary' || type === 'eventsCalendar'
      };
    });
    this.availableWidgetTypes = this.widgetAdapter.getAllWidgetTypes();
  }

  ngOnInit() {
    if (!this.options) {
      this.initializeGridsterOptions();
    }

    this.hydrateDashboardFromMemoryOrCache();

    if (this.shareLinkGuid) {
      this.shareLinkContext.setShareLink(this.shareLinkGuid);
    }

    if (!this.isPublicShareView) {
      loadCateringGroupFilterOptions(this.http).subscribe({
        next: (options) => {
          this.cateringGroupFilterOptions = options;
          this.cdr.detectChanges();
        },
        error: () => {
          this.cateringGroupFilterOptions = [];
        }
      });
    }
    
    // Get available widget types
    this.availableWidgetTypes = this.widgetAdapter.getAllWidgetTypes();
    
    // Get widget definitions
    this.availableWidgetDefinitions = this.availableWidgetTypes.map(type => {
      const config = this.widgetAdapter.getWidgetConfig(type);
      return {
        type,
        title: config.title,
        description: config.description,
        icon: config.icon,
        defaultSize: config.defaultSize,
        defaultDisplayType: config.defaultDisplayType,
        defaultFields: config.defaultFields,
        visible: type === 'userProgress' || type === 'userProgressStats' || type === 'locationProgress' || type === 'simpleNumber' || type === 'cateringSummary' || type === 'eventsCalendar'
      };
    });
    
    // If no widget types are available, add default ones for testing
    if (this.availableWidgetDefinitions.length === 0) {
      this.availableWidgetDefinitions = [
        {
          type: 'userProgress',
          title: 'Bruger Fremgang',
          description: 'Viser bruger fremgang og status',
          icon: 'person',
          defaultSize: { cols: 6, rows: 4 },
          defaultDisplayType: 'table',
          defaultFields: ['name', 'path', 'registeredPercentage', 'percentComplete', 'station'],
          visible: true // Show in add widget dialog
        },
        {
          type: 'locationProgress',
          title: 'Lokation Fremgang',
          description: 'Viser fremgang for alle lokationer',
          icon: 'location_on',
          defaultSize: { cols: 6, rows: 4 },
          defaultDisplayType: 'table',
          defaultFields: ['name', 'userCount', 'averageProgress', 'completionRate'],
          visible: true // Show in add widget dialog
        },
        {
          type: 'userProgressStats',
          title: 'Bruger Statistik',
          description: 'Viser detaljeret bruger statistik og fremgang',
          icon: 'bar_chart',
          defaultSize: { cols: 6, rows: 4 },
          defaultDisplayType: 'chart',
          defaultFields: ['name', 'path', 'registeredPercentage', 'percentComplete', 'station', 'trend', 'lastActivity'],
          visible: true // Show in add widget dialog
        },
        {
          type: 'simpleNumber',
          title: 'Simpelt Tal',
          description: 'Viser et enkelt tal med titel',
          icon: 'looks_one',
          defaultSize: { cols: 3, rows: 2 },
          defaultDisplayType: 'number',
          defaultFields: ['value', 'title'],
          visible: true // Show in add widget dialog
        },
        {
          type: 'status',
          title: 'Status Oversigt',
          description: 'Viser overordnet status information',
          icon: 'pie_chart',
          defaultSize: { cols: 4, rows: 4 },
          defaultDisplayType: 'chart',
          defaultFields: ['status', 'count'],
          visible: false // Hide from add widget dialog
        },
        {
          type: 'quickLinks',
          title: 'Hurtig Links',
          description: 'Viser hurtig links til andre systemer',
          icon: 'link',
          defaultSize: { cols: 4, rows: 3 },
          defaultDisplayType: 'buttons',
          defaultFields: ['title', 'url', 'icon'],
          visible: false // Hide from add widget dialog
        }
      ];
    }
    
    if (this.isPublicShareView || this.isShareLinkAdminFiltersView) {
      this.loadShareLinkDashboard();
    } else {
      this.ensureServerDashboardLoad();

      this.subscription.add(
        this.store.select(selectCurrentUserId).pipe(
          distinctUntilChanged(),
          filter((userId): userId is string => !!userId)
        ).subscribe((userId) => {
          this.currentUserId = userId;
          if (this.dashboard.length === 0) {
            this.loadUserDashboardConfig(userId);
          }
        })
      );

      this.subscription.add(
        this.store.select(selectIsAuthInitialized).pipe(
          filter((initialized) => initialized),
          take(1)
        ).subscribe(() => {
          if (this.dashboard.length === 0) {
            this.ensureServerDashboardLoad(true);
          }
        })
      );

      this.subscription.add(
        timer(6000).pipe(take(1)).subscribe(() => {
          if (this.isDestroyed) {
            return;
          }
          if (this.dashboard.length === 0) {
            this.ensureServerDashboardLoad(true);
          }
          if (this.initializing) {
            this.initializing = false;
            this.cdr.markForCheck();
          }
        })
      );
    }

    // Filter dialogs need these lists. A public share link has no session and must not
    // call /paths, /user-groups or org settings.
    if (!this.isPublicShareView) {
      this.loadEducationalPaths();
      this.loadUserGroups(true);
      this.loadGroupsEnabled();
    }
  }

  private loadGroupsEnabled(): void {
    if (this.isPublicShareView) return;
    this.loadUserGroups(true);
    const obs = this.componentDataSourceService.executeDataSource('duna_groups_enabled', {});
    if (!obs) {
      this.cdr.detectChanges();
      return;
    }
    obs.subscribe({
      next: (res: { visible?: boolean }) => {
        this.groupsEnabled = res?.visible === true;
        this.cdr.detectChanges();
      },
      error: () => {
        this.groupsEnabled = this.userGroups.length > 0;
        this.cdr.detectChanges();
      }
    });
  }

  private readonly selectionDialogConfig = {
    width: '860px',
    maxWidth: '95vw',
    height: '680px',
    maxHeight: '90vh',
    panelClass: ['centered-dialog', 'dashboard-filter-picker-dialog'],
    autoFocus: false,
    restoreFocus: false,
    hasBackdrop: true,
    disableClose: false,
  };

  private resolveDashboardWidget(widget: WidgetData): WidgetData {
    if (!widget?.id) return widget;
    return this.dashboard.find(w => w.id === widget.id) ?? widget;
  }

  private loadUserGroups(force = false): void {
    if (this.isPublicShareView) return;
    if (this.userGroupsLoaded && !force) return;
    this.fetchUserGroupsList().subscribe({
      next: (groups) => {
        this.userGroups = groups;
        this.userGroupsLoaded = true;
        this.cdr.detectChanges();
      },
      error: () => {
        this.userGroupsLoaded = false;
      }
    });
  }

  private fetchUserGroupsList(): Observable<{ id: number; name: string }[]> {
    const httpObs = this.http.get<any>(`${environment.apiUrl}/user-groups`, {
      params: { pageSize: '1000', page: '1', pageIndex: '0' }
    }).pipe(map((body) => this.normalizeUserGroupsList(body)));

    const dsObs = this.componentDataSourceService.executeDataSource('duna_user_groups', {
      filters: [
        { column: 'pageIndex', value: 0, operator: '=' },
        { column: 'pageSize', value: 1000, operator: '=' }
      ]
    });

    if (!dsObs) {
      return httpObs.pipe(catchError(() => of([])));
    }

    return httpObs.pipe(
      switchMap((groups) => (groups.length > 0 ? of(groups) : dsObs.pipe(
        map((result: any) => this.normalizeUserGroupsList(result)),
        catchError(() => of([]))
      ))),
      catchError(() => dsObs.pipe(
        map((result: any) => this.normalizeUserGroupsList(result)),
        catchError(() => of([]))
      ))
    );
  }

  private normalizeUserGroupsList(result: any): { id: number; name: string }[] {
    const items = Array.isArray(result?.items)
      ? result.items
      : (Array.isArray(result?.data)
        ? result.data
        : (Array.isArray(result) ? result : []));
    return (items || [])
      .map((g: any) => ({
        id: Number(g.group_id ?? g.id),
        name: (g.name || `Gruppe ${g.group_id ?? g.id}`).trim()
      }))
      .filter((g: { id: number; name: string }) => Number.isFinite(g.id) && g.id > 0);
  }

  private fetchEducationalPathsList(): Observable<any[]> {
    const dsObs = this.componentDataSourceService.executeDataSource('educational_paths', {
      filters: [
        { column: 'pageIndex', value: 0, operator: '=' },
        { column: 'pageSize', value: 2000, operator: '=' }
      ]
    });
    if (!dsObs) {
      return getEducationalPaths(this.http);
    }
    return dsObs.pipe(
      map((result: any) => this.normalizeEducationalPathsFromResult(result)),
      switchMap((paths) => (paths.length > 0 ? of(paths) : getEducationalPaths(this.http))),
      catchError(() => getEducationalPaths(this.http))
    );
  }

  private normalizeEducationalPathsFromResult(result: any): any[] {
    const items = Array.isArray(result?.items)
      ? result.items
      : (Array.isArray(result?.data) ? result.data : (Array.isArray(result) ? result : []));
    return (items || []).map((p: any) => ({
      pathId: p.pathId ?? p.path_id ?? p.id,
      path_id: p.path_id ?? p.pathId ?? p.id,
      id: p.pathId ?? p.path_id ?? p.id,
      name: p.name ?? '',
      full_name: p.full_name ?? p.fullName ?? p.name ?? '',
      fullName: p.fullName ?? p.full_name ?? p.name ?? ''
    })).filter((p: any) => p.id != null && p.id !== '');
  }

  private mapEducationalPathList(paths: any[]): { id: string | number; name: string; full_name?: string }[] {
    return (paths || []).map((p: any) => ({
      id: p.pathId ?? p.path_id ?? p.id,
      name: this.getEducationalPathDisplayName(p),
      full_name: p.full_name ?? p.fullName ?? p.name ?? ''
    }));
  }

  private loadEducationalPaths(force = false): void {
    if (this.isPublicShareView) return;
    if (this.educationalPathsLoaded && !force && this.educationalPaths.length > 0) return;
    this.fetchEducationalPathsList().subscribe({
      next: (paths) => {
        this.educationalPaths = this.mapEducationalPathList(paths);
        this.educationalPathsLoaded = true;
        this.cdr.detectChanges();
      },
      error: () => {
        this.educationalPathsLoaded = false;
      }
    });
  }

  private openSelectionDialogAfterFilterClose(openFn: () => void): void {
    this.closeFilterDialog();
    setTimeout(openFn, 100);
  }

  private normalizeFilterValue(key: string, value: any): any {
    if (value === 'all' || value === null || value === undefined) {
      return '';
    }
    if (key === 'stations' && Array.isArray(value)) {
      return value.map((s) => String(s).trim()).filter((s) => s && s !== 'all');
    }
    if (key === 'groupId' || key === 'catering_group') {
      if (value === '') return '';
      const num = typeof value === 'number' ? value : parseInt(String(value), 10);
      return Number.isInteger(num) && num > 0 ? num : '';
    }
    return value;
  }

  private ensureFilterReferenceData(widget: WidgetData): void {
    const needsGroups = ['userProgress', 'userProgressStats', 'locationProgress', 'simpleNumber'].includes(widget.type);
    const needsPaths = needsGroups;
    if (needsGroups) {
      this.loadUserGroups(true);
    }
    if (needsPaths && (!this.educationalPathsLoaded || this.educationalPaths.length === 0)) {
      this.loadEducationalPaths(true);
    }
  }

  public loadAllLocationsForWidget(widget: WidgetData): void {
    if (this.allWidgetLocations.has(widget.id) || this.allWidgetLocationsLoading.get(widget.id)) return;
    this.allWidgetLocationsLoading.set(widget.id, true);

    const request = createListRequest({ page: 1, pageSize: 1000, includeFilters: true });
    getUserProgressData(this.http, request).subscribe({
      next: (response) => {
        const fromFilters = extractLocationNamesFromListFilters(response.filters);
        const stations =
          fromFilters.length > 0
            ? fromFilters.sort()
            : ([...new Set(
                (response.data || [])
                  .map((u: { station?: string }) => u.station)
                  .filter((s: string | undefined): s is string => typeof s === 'string' && s !== 'Unknown')
              )].sort() as string[]);
        this.allWidgetLocations.set(widget.id, stations);
        this.allWidgetLocationsLoading.set(widget.id, false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.allWidgetLocationsLoading.set(widget.id, false);
      }
    });
  }

  public getLocationsForWidget(widget: WidgetData): string[] {
    return this.allWidgetLocations.get(widget.id) || [];
  }

  public isLocationsLoading(widget: WidgetData): boolean {
    return this.allWidgetLocationsLoading.get(widget.id) === true;
  }

  public getEducationName(id: string | number): string {
    if (id === undefined || id === null || id === '' || id === 'all') return '';
    const match = this.educationalPaths.find(p => p.id == id);
    return match ? match.name : String(id);
  }

  public getGroupName(id: string | number): string {
    if (id === undefined || id === null || id === '' || id === 'all') return '';
    const match = this.userGroups.find(g => g.id == id);
    return match ? match.name : String(id);
  }

  private getEducationalPathDisplayName(p: any): string {
    const fullName = (p.full_name ?? p.fullName ?? '').trim();
    const shortName = (p.name ?? '').trim();
    if (fullName) return fullName;
    if (shortName) return shortName;
    return String(p.pathId ?? p.id ?? '');
  }

  public clearAllFilters(widget: WidgetData): void {
    if (!widget.settings) return;
    widget.settings.filters = {};
    widget.currentPage = 1;
    widget.hasMoreData = true;
    this.updateProgressListLoadingMore(widget, false);
    this.loadWidgetData(widget);
    this.cdr.detectChanges();
  }

  /**
   * Continue initialization using previously loaded data
   * This method is called when we detect a stable initialization has already completed
   */
  private continueInitialization(): void {
    
    // Initialize gridster options
    this.initializeGridsterOptions();
    
    // Use the static dashboard data if available
    if (DunaDashboardComponent.dashboardData && DunaDashboardComponent.dashboardData.length > 0) {
      this.dashboard = [...DunaDashboardComponent.dashboardData];
    } else {
      // Fallback to default dashboard
      this.createDefaultDashboardImmediate();
    }
    
    // Mark initialization as complete
    this.loadDashboardComplete = true;
    this.initializing = false;
    
    // Setup other necessary components
    this.setupWidgetDefinitions();
    this.setupResizeListeners();
    
    // Make sure grid options are properly synchronized
    this.syncGridsterOptionsWithEditMode();
    
    // Force change detection
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    this.observeDashboardLayout();
    this.scheduleGridsterLayout();
    // Set up resize observer for responsive charts
    this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));
    
    // Set up intersection observer for lazy loading charts
    this.setupIntersectionObserver();
    
    // Listen for changes to chart canvases
    this.chartCanvases.changes.subscribe(() => this.observeChartContainers());
    this.statusChartCanvases.changes.subscribe(() => this.observeChartContainers());
    
    /* 
     * Disable automatic debug on login to prevent excessive API calls 
     * when component is initialized and immediately destroyed
     */
    
    /*
    setTimeout(() => {
      this.debugUserProgressData();
    }, 2000); // Wait 2 seconds after view init to run debug
    */
  }
  
  ngOnDestroy() {
    // Mark as destroyed to prevent further operations
    this.isDestroyed = true;
    this.closeFilterDialog();
    this.disposeSettingsOverlay(false);
    if (this.shareLinkGuid) {
      this.shareLinkContext.clear();
    }
    
    // Save the state before destruction to ensure we can recover
    if (this.dashboard && this.dashboard.length > 0) {
      
      try {
        // Save to localStorage for persistence across page reloads
        localStorage.setItem('dunaWidgets', JSON.stringify(this.dashboard));
        
        // Update static storage to maintain state across recreations
        DunaDashboardComponent.dashboardData = [...this.dashboard];
        DunaDashboardComponent.widgetDataLoaded = true;
      } catch (e) {
        console.error('[DASHBOARD-COMPONENT] Error saving dashboard state:', e);
      }
    }
    
    // Update static tracking for recreation prevention
    DunaDashboardComponent.lastDestroyTime = Date.now();
    

    // Cancel any pending timeouts
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
    
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }

    this.progressListLoadMoreUiTimers.forEach((timer) => clearTimeout(timer));
    this.progressListLoadMoreUiTimers.clear();
    this.progressListLoadMoreUiVisible.clear();
    
    // Clean up any resize timeouts
    this.resizeTimeouts.forEach(timeout => {
      clearTimeout(timeout);
    });
    this.resizeTimeouts.clear();
    
    // Clean up charts and observers
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.gridLayoutObserver) {
      this.gridLayoutObserver.disconnect();
      this.gridLayoutObserver = null;
    }
    if (this.gridLayoutTimer) {
      clearTimeout(this.gridLayoutTimer);
      this.gridLayoutTimer = null;
    }
    
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    
    // Track charts being destroyed
    this.initializedCharts.forEach(chartId => {
      this.destroyedCharts.add(chartId);
    });
    
    // Destroy all chart instances via the chart service
    if (this.chartService) {
      this.chartService.destroyAllCharts();
      this.chartService.reset(); // Use the new reset method
    }
    this.initializedCharts.clear();
    this.chartInitializationInProgress.clear();
    
    // Clear global chart instances
    DunaDashboardComponent.chartInstances.clear();
    
    // Unsubscribe from all subscriptions
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    // Remove resize listener
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }
    
    // Don't reset hasInitialized flag during recreation cycles
    if (DunaDashboardComponent.recreationCount < DunaDashboardComponent.MAX_RECREATIONS 
        && !DunaDashboardComponent.stableInitComplete) {
      DunaDashboardComponent.hasInitialized = false;
      DunaDashboardComponent.initializationInProgress = false;
    } else {
    }
    
  }

  // Add method to fix template error and help with debugging
  public logWidgetDefinitions() {
    
    // Try to force enable all widgets manually
    const allDefinitions = this.widgetAdapter.getAllWidgetTypes().map(type => {
      const config = this.widgetAdapter.getWidgetConfig(type);
      return {
        type,
        title: config.title,
        description: config.description,
        icon: config.icon,
        defaultSize: config.defaultSize,
        defaultDisplayType: config.defaultDisplayType,
        defaultFields: config.defaultFields
      };
    });
    
    // Refresh definitions
    this.availableWidgetDefinitions = allDefinitions;
    this.availableWidgetTypes = this.widgetAdapter.getAllWidgetTypes();
    
    // Test opening dialog directly
    if (this.dialog) {
      const testDialog = this.dialog.open(AddDashboardItemDialogComponent, {
        width: '400px',
        data: {
          title: 'Test Dialog',
          widgetTypes: [
            { key: 'test', name: 'Test Widget', description: 'Testing dialog display', icon: 'bug_report' }
          ]
        }
      });
    } else {
      console.error('[DASHBOARD-COMPONENT] Dialog service is not available');
    }
    
    alert(`Widget info: ${this.availableWidgetDefinitions.length} definitions found.
Check console for details.`);
    
    // Force a change detection cycle
    this.cdr.detectChanges();
  }

  public trackByTileId(index: number, widget: WidgetData): string {
    return widget.id;
  }

  /**
   * Get properly formatted gridster item for a widget
   */
  public getGridsterItem(widget: WidgetData): GridsterItemConfig {
    const cols = widget.position?.cols ?? widget.cols ?? 6;
    const rows = widget.position?.rows ?? widget.rows ?? 4;
    const x = widget.position?.x ?? widget.x ?? 0;
    const y = widget.position?.y ?? widget.y ?? 0;

    return widget.position ?? {
      x,
      y,
      cols,
      rows,
      id: widget.id
    };
  }

  private initializeGridsterOptions() {
    
    // Create a safe api object first to avoid "Cannot set properties of undefined" errors
    const safeApi = {
      resize: () => {
      },
      optionsChanged: () => {
      },
      getNextPossiblePosition: (newItem: GridsterItemConfig) => {
        return true;
      }
    };
    this.gridsterStubApi = safeApi;
    this.lastLaidOutWidth = 0;
    this.lastLaidOutWidgetCount = -1;
    
    this.options = {
      gridType: GridType.VerticalFixed, // Use Fixed grid type to prevent auto-expansion
      fixedColWidth: 100, // Required for VerticalFixed grid type
      fixedRowHeight: 125, // Fixed row height at 100px - this ensures consistent row heights
      compactType: CompactType.None,
      margin: 10,
      outerMargin: true,
      outerMarginTop: 10,
      outerMarginRight: 10,
      outerMarginBottom: 10,
      outerMarginLeft: 10,
      useTransformPositioning: false, // Disable transform positioning to prevent auto-expansion
      mobileBreakpoint: 640,
      minCols: 12,
      maxCols: 12, // Enforce exactly 12 columns
      minRows: 1,
      maxRows: 100,
      maxItemCols: 12, // Limit items to max 12 columns
      minItemCols: 1,
      maxItemRows: 50,
      minItemRows: 1,
      maxItemArea: 2500,
      minItemArea: 1,
      defaultItemCols: 6, // Default widget width is half the grid
      defaultItemRows: 4, // Default height is 4 rows
      keepFixedHeightInMobile: true, // Keep fixed height to prevent scrollbars
      keepFixedWidthInMobile: false,
      scrollSensitivity: 10,
      scrollSpeed: 20,
      enableEmptyCellClick: false,
      enableEmptyCellContextMenu: false,
      enableEmptyCellDrop: false,
      enableEmptyCellDrag: false,
      enableOccupiedCellDrop: false,
      emptyCellDragMaxCols: 50,
      emptyCellDragMaxRows: 50,
      ignoreMarginInRow: false,
      draggable: {
        enabled: this.editMode,
        ignoreContent: false,       // important: prevents drag from starting on grid content
        dragHandleClass: 'drag-handle', // specify the class for the drag handle
        dropOverItems: true,        // Allow dropping on other items
        dropOverItemsCallback: undefined
      },
      resizable: {
        enabled: this.editMode,
      },
      swap: false, // Disable swap to prevent auto-expansion
      pushItems: false, // Disable push items to prevent auto-expansion
      disablePushOnDrag: true, // Disable push on drag
      disablePushOnResize: true, // Disable push on resize
      pushDirections: { north: false, east: false, south: false, west: false }, // Disable all push directions
      pushResizeItems: false, // Disable push resize items
      displayGrid: DisplayGrid.OnDragAndResize,
      disableWindowResize: true, // Disable window resize to prevent auto-expansion
      disableWarnings: false,
      scrollToNewItems: false, // Disable scroll to new items to prevent scrollbars
      
      // Always define api to prevent null reference errors
      api: safeApi,
      
      // Other callbacks
      initCallback: (gridsterInstance) => {
        this.onGridsterInitialized(gridsterInstance);
      },
      itemChangeCallback: (item: GridsterItemConfig) => {
        this.onItemChange(item);
        // Store the past layout whenever an item position changes
        this.storePastLayout();
      },
      itemResizeCallback: (item: GridsterItemConfig) => {
        this.onItemResize(item);
        // Store the past layout whenever an item is resized
        this.storePastLayout();
      },
      gridSizeChangedCallback: () => {
        // Handle grid size changes (responsive behavior)
        
        // Force change detection to update the UI
        this.cdr.detectChanges();
      }
    };
  }

  /**
   * Gridster measures the container during its first pass. On a cold load that
   * width is often still 0, so every widget is laid out with no size until a
   * manual refresh. Re-run the layout once the container actually has a width
   * and the widget list is in the view.
   */
  private observeDashboardLayout(): void {
    const element = this.dashboardOuter?.nativeElement;
    if (!element || this.gridLayoutObserver) {
      return;
    }
    this.gridLayoutObserver = new ResizeObserver(() => {
      this.ngZone.run(() => this.scheduleGridsterLayout());
    });
    this.gridLayoutObserver.observe(element);
  }

  private scheduleGridsterLayout(): void {
    if (this.isDestroyed) {
      return;
    }
    this.gridLayoutAttempts = 0;
    if (this.gridLayoutTimer) {
      clearTimeout(this.gridLayoutTimer);
    }
    this.gridLayoutTimer = setTimeout(() => {
      this.gridLayoutTimer = null;
      this.resizeGridsterToContainer();
    }, 0);
  }

  private resizeGridsterToContainer(): void {
    if (this.isDestroyed) {
      return;
    }
    const api = this.options?.['api'];
    const width = this.dashboardOuter?.nativeElement.clientWidth ?? 0;
    if (!api || api === this.gridsterStubApi || width <= 0) {
      if (this.gridLayoutAttempts < DunaDashboardComponent.GRID_LAYOUT_MAX_ATTEMPTS) {
        this.gridLayoutAttempts += 1;
        this.gridLayoutTimer = setTimeout(() => this.resizeGridsterToContainer(), 50);
      }
      return;
    }

    const widgetCount = this.dashboard?.length ?? 0;
    if (width === this.lastLaidOutWidth && widgetCount === this.lastLaidOutWidgetCount) {
      this.gridLayoutAttempts = 0;
      return;
    }

    this.lastLaidOutWidth = width;
    this.lastLaidOutWidgetCount = widgetCount;
    this.gridLayoutAttempts = 0;
    try {
      api.resize?.();
      api.optionsChanged?.();
    } catch (error) {
      console.error('[DASHBOARD-COMPONENT] Gridster layout refresh failed', error);
    }
  }

  public loadDashboard(forceReload = false) {
    
    // If loop breaking is active, show error state
    if (DunaDashboardComponent.breakingLoop || DunaDashboardComponent.forceStableView) {
      this.loadDashboardComplete = true;
      return;
    }

    const loadSeq = ++this.dashboardLoadSeq;

    if (forceReload) {
      this.loadDashboardComplete = false;
      this.initializing = false;
      DunaDashboardComponent.widgetDataLoaded = false;
      DunaDashboardComponent.dashboardData = [];
      this.dashboard = [];
      this.dashboardService.clearAllDashboardCache();
    }
    
    // Always load from service to ensure backend is consulted
    this.loadFromService(forceReload, loadSeq);
  }

  public applyDashboardFromConfig(dashboardConfig: DashboardConfigDTO): void {
    if (!dashboardConfig?.widgets?.length) {
      console.warn('[DASHBOARD-COMPONENT] applyDashboardFromConfig called without widgets');
      this.loadDashboard(true);
      return;
    }

    const migratedWidgets = this.migrateWidgetDataSources(this.mapDashboardConfigToWidgets(dashboardConfig));

    DunaDashboardComponent.dashboardData = [...migratedWidgets];
    DunaDashboardComponent.widgetDataLoaded = true;
    this.dashboard = migratedWidgets;
    this.loadDashboardComplete = true;
    this.initializing = false;
    this.showEmptyState = false;

    this.dashboardService.cacheCurrentDashboard(dashboardConfig);
    this.setLoadedDashboard(dashboardConfig);
    this.cdr.detectChanges();
    this.scheduleGridsterLayout();

    setTimeout(() => {
      if (!this.isDestroyed) {
        this.loadWidgetsWithDelay();
      }
    }, 300);

    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('duna-request-scaling-update'));
    }
  }

  private setLoadedDashboard(config: DashboardConfigDTO | null): void {
    this.loadedDashboard = config;
    this.loadedDashboardCanEditTemplate = config?.canEditTemplate === true;
  }

  private mapDashboardConfigToWidgets(dashboardConfig: DashboardConfigDTO): WidgetData[] {
    return (dashboardConfig.widgets || []).map((widgetConfig) => {
      const filters = widgetConfig.defaultFilters || widgetConfig.filters || {};
      const rawPosition = widgetConfig.position as { x?: number; y?: number; cols?: number; rows?: number } | undefined;
      const rawSize = (widgetConfig as { size?: { cols?: number; rows?: number } }).size;
      const cols = rawPosition?.cols ?? rawSize?.cols ?? 3;
      const rows = rawPosition?.rows ?? rawSize?.rows ?? 3;
      const x = rawPosition?.x ?? 0;
      const y = rawPosition?.y ?? 0;

      return {
        id: widgetConfig.id,
        type: widgetConfig.widgetType,
        title: widgetConfig.title,
        dataSource: widgetConfig.dataSource || this.getDataSourceIdForWidgetType(widgetConfig.widgetType),
        position: {
          x,
          y,
          cols,
          rows,
          id: widgetConfig.id
        },
        cols,
        rows,
        x,
        y,
        defaultFilters: filters,
        settings: {
          category: widgetConfig.widgetType,
          filters,
          fields: ['name', 'path', 'registeredPercentage', 'percentComplete', 'station'],
          displayType: 'table'
        },
        isLoading: false,
        initialLoadComplete:
          widgetConfig.widgetType === 'userProgress' ||
          widgetConfig.widgetType === 'locationProgress'
            ? false
            : true,
        data: widgetConfig.widgetType === 'userProgress'
          ? { users: [], totalCount: 0, filters: {} }
          : widgetConfig.widgetType === 'locationProgress'
            ? { locations: [], totalCount: 0, filters: {} }
            : { items: [] }
      } as WidgetData;
    });
  }

  private loadFromService(forceReload = false, loadSeq = this.dashboardLoadSeq) {
    
    // Add a flag for service loading in progress
    let serviceLoadingComplete = false;
    
    // Get dashboard directly from the /current endpoint to avoid reset-to-default being called
    const dashboardSubscription = this.dashboardService.fetchCurrentDashboardDirectly()
      .pipe(
        // Add a longer timeout and retry to handle potential issues
        timeout(15000), // Increase from 5s to 15s to prevent timeout errors
        retry(2), // Increase retry attempts
        catchError(err => {
          console.error('[DASHBOARD-COMPONENT] Error loading dashboard:', err);
          // Don't create a default dashboard client-side, let the API handle it
          serviceLoadingComplete = true;
          // Return empty observable to continue the chain
          return of(null);
        })
      )
      .subscribe({
        next: (dashboardConfig) => {
          if (loadSeq !== this.dashboardLoadSeq) {
            return;
          }

          if (dashboardConfig) {
            
            // Check if we've been destroyed while loading
            if (this.isDestroyed) {
              return;
            }
            
            // Check if loading is already complete from another source
            if (!forceReload && (serviceLoadingComplete || this.loadDashboardComplete)) {
              return;
            }
            
            // Convert dashboard config to widget data
            const widgets = this.mapDashboardConfigToWidgets(dashboardConfig);
            
            // Apply data source migration to ensure compatibility
            const migratedWidgets = this.migrateWidgetDataSources(widgets);
            
            // Only update if we have widgets and haven't already loaded
            if (migratedWidgets.length > 0 && (!this.loadDashboardComplete || forceReload)) {
              serviceLoadingComplete = true;
              
              // Store in static storage for future instances
              DunaDashboardComponent.dashboardData = [...migratedWidgets];
              DunaDashboardComponent.widgetDataLoaded = true;
              
              this.dashboard = migratedWidgets;
              
              this.setLoadedDashboard(dashboardConfig);
              this.dashboardService.cacheCurrentDashboard(dashboardConfig);
              
              // Set initialization complete
              this.loadDashboardComplete = true;
              this.initializing = false;
              
              // Force change detection to update UI
              this.cdr.detectChanges();
              
              // Load widget data with a slight delay to ensure the layout has initialized
              setTimeout(() => {
                if (!this.isDestroyed) {
                  this.loadWidgetsWithDelay();
                }
              }, 300);
              
              // Mark initial layout as saved to prevent duplicate initialization work
              if (!this.initialLayoutSaved) {
                this.initialLayoutSaved = true;
              }
            }
          } else if (!serviceLoadingComplete && !this.loadDashboardComplete) {
            // Create a default dashboard manually
            this.resetDashboardManually(true, false);
            serviceLoadingComplete = true;
            this.loadDashboardComplete = true;
            this.initializing = false;
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          if (!serviceLoadingComplete && !this.loadDashboardComplete) {
            console.error('[DASHBOARD-COMPONENT] Failed to load dashboard:', error);
            serviceLoadingComplete = true;
            
            // Create a default dashboard manually instead of showing error state
            this.resetDashboardManually(true, false);
            this.loadDashboardComplete = true;
            this.initializing = false;
            this.cdr.detectChanges();
          }
        },
        complete: () => {
          // Ensure we always mark initialization as complete
          this.loadDashboardComplete = true;
          this.initializing = false;
          serviceLoadingComplete = true;
          this.cdr.detectChanges();
        }
      });
    
    // Store subscription for cleanup
    this.subscription.add(dashboardSubscription);
    
    // Set a safety timeout to ensure we always mark loading as complete
    setTimeout(() => {
      if (!serviceLoadingComplete) {
        serviceLoadingComplete = true;
        
        // If we haven't loaded anything yet, create default dashboard
        if (!this.loadDashboardComplete) {
          this.resetDashboardManually(true, false);
          this.loadDashboardComplete = true;
          this.initializing = false;
          this.cdr.detectChanges();
        }
      }
    }, 20000); // 20 seconds safety timeout
  }
  
  private initRetryTracking(widget: WidgetData): void {
    if (!widget) return;
    widget.retryCount = 0;
    widget.lastRetryTime = 0;
  }

  /**
   * Debug user progress data without triggering any retries
   * This is a safer version used in automatic debugging sequences
   */
  private debugUserProgressDataWithoutRetries(): void {
    
    // Check if we have any dashboard widgets
    if (!this.dashboard || this.dashboard.length === 0) {
      return;
    }
    
    // Find any userProgress widgets
    const userProgressWidgets = this.dashboard.filter(w => w.type === 'userProgress');
    
    if (userProgressWidgets.length === 0) {
      return;
    }
    
    // Log data for each widget
    userProgressWidgets.forEach((widget, index) => {
      
      if (widget.data) {
        
        // Log first user if available
        if (widget.data.users && widget.data.users.length > 0) {
        } else {
        }
      } else {
        // Initialize data silently without retrying
        widget.data = { users: [], totalCount: 0, filters: {} };
      }
      
      // Log widget settings
    });
  }

  private loadWidgetsWithDelay(): void {
    
    if (this.isDestroyed) {
      return;
    }
    
    if (DunaDashboardComponent.initializationInProgress) {
      
      // Force reset the initialization flag to false if it's stuck
      DunaDashboardComponent.initializationInProgress = false;
    }
    
    // Safety check - if dashboard is empty, don't try to load widgets
    if (!this.dashboard || this.dashboard.length === 0) {
      return;
    }
    
    
    // Initialize retry tracking for each widget, but don't set loading state yet
    this.dashboard.forEach(widget => {
      // Initialize retry tracking
      this.initRetryTracking(widget);
      
      // Make sure each widget has settings initialized from defaultFilters if missing
      if (!widget.settings && widget.defaultFilters) {
        widget.settings = {
          category: widget.type,
          filters: { ...widget.defaultFilters },
          fields: [],
          displayType: 'table'
        };
      } else if (!widget.settings) {
        widget.settings = {
          category: widget.type,
          filters: {},
          fields: [],
          displayType: 'table'
        };
      }
      
      // IMPORTANT: Show loading for list widgets until first fetch completes
      if (widget.type === 'userProgress' || widget.type === 'locationProgress') {
        widget.initialLoadComplete = false;
        widget.isLoading = true;
      } else {
        widget.isLoading = false;
      }
      
      // Log widget settings for debugging
    });
    
    // Force change detection
    this.cdr.detectChanges();
    
    // Then load each widget with a delay to prevent UI blocking
    this.dashboard.forEach((widget, index) => {
      
      setTimeout(() => {
        // Check again if component is destroyed before loading data
        if (this.isDestroyed) {
          return;
        }
        
        // Only now set the loading state to true, right before we actually load the data
        widget.isLoading = true;
        this.cdr.detectChanges();
        
        // Actually load the data for this widget
        this.loadWidgetData(widget);
      }, index * 200);
    });
    
    // Set a timeout to mark loading as complete after all widgets have had a chance to load
    const finalDelay = Math.max(500, this.dashboard.length * 200 + 500);
    setTimeout(() => {
      if (!this.isDestroyed) {
        this.loadDashboardComplete = true;
        
        // Ensure any widgets still in loading state are marked as not loading
        this.dashboard.forEach(widget => {
          if (widget.isLoading && widget.initialLoadComplete !== false) {
            widget.isLoading = false;

            if (!widget.data) {
              widget.data = widget.type === 'userProgress'
                ? { users: [], totalCount: 0, filters: {} }
                : widget.type === 'locationProgress'
                  ? { locations: [], totalCount: 0, filters: {} }
                  : { items: [], totalCount: 0, filters: {} };
            }
          }
        });
        
        // Force change detection to update UI
        this.cdr.detectChanges();
        
        // Debug the user progress data after loading is complete, but only once and without retries
        setTimeout(() => {
          if (!this.isDestroyed) {
            this.debugUserProgressDataWithoutRetries();
          }
        }, 500);
      }
    }, finalDelay);
  }
  
  /**
   * Reset dashboard only if not already reset
   */
  private resetDashboardOnce() {
    // Skip if component is destroyed or initialization is in progress
    if (this.isDestroyed || DunaDashboardComponent.initializationInProgress) {
      return;
    }
    
    if (!this.dashboardInitialized || this.dashboard.length === 0) {
      // Don't save to API during initialization
      this.resetDashboardManually(false, false);
      this.dashboardInitialized = true;
    }
  }

  /**
   * Helper to ensure a widget has a proper position object for gridster
   */
  private ensureWidgetHasPosition(widget: WidgetData): WidgetData {
    // Log incoming widget size
    
    // If position already exists, make sure cols and rows are copied to top level properties
    if (widget.position) {
      widget.x = widget.position.x;
      widget.y = widget.position.y;
      widget.cols = widget.position.cols;
      widget.rows = widget.position.rows;
      return widget;
    }
    
    // Check for existing size properties at top level
    if (widget.cols && widget.rows) {
      // Create position from existing size
      widget.position = {
        x: widget.x || 0,
        y: widget.y || 0,
        cols: widget.cols,
        rows: widget.rows,
        id: widget.id
      };
      return widget;
    }
    
    // Get default size from registry or fallback
    const widgetConfig = this.dashboardDataRegistry.getWidgetConfig(widget.type);
    
    // For userProgress widgets, force 6x4 size
    let defaultSize = widgetConfig?.defaultSize || { cols: 2, rows: 2 };
    if (widget.type === 'userProgress') {
      defaultSize = { cols: 6, rows: 4 };
    }
    
    // Create position object from x, y, cols, rows properties or use defaults
    const x = widget.x !== undefined ? widget.x : 0;
    const y = widget.y !== undefined ? widget.y : 0;
    const cols = defaultSize.cols;
    const rows = defaultSize.rows;
    
    // Update the widget itself
    widget.x = x;
    widget.y = y;
    widget.cols = cols;
    widget.rows = rows;
    
    widget.position = {
      x,
      y,
      cols,
      rows,
      id: widget.id
    };
    
    return widget;
  }

  /**
   * Reset dashboard to default template
   * This will clear all customizations and revert to the system default template
   * WARNING: This will permanently delete all user customizations!
   */
  public resetDashboard(): void {
    // Show confirmation dialog with clear warning
    const dialogRef = this.dialog.open(ConfirmResetDialogComponent, {
      width: '500px',
      data: {
        title: 'Nulstil Dashboard',
        message: 'Er du sikker på, at du vil nulstille dit dashboard til standard skabelonen?',
        details: [
          '• Alle dine tilpasninger vil blive slettet permanent',
          '• Widget-positioner og indstillinger går tabt',
          '• Du vil få standard skabelonen tilbage',
          '• Denne handling kan ikke fortrydes'
        ],
        confirmText: 'Ja, nulstil',
        cancelText: 'Annuller',
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.performReset();
      }
    });
  }

  /**
   * Perform the actual reset operation
   */
  private performReset(): void {
    
    // Store current layout before reset
    this.storePastLayout();
    
    // Call the reset-to-default API endpoint with manual reset flag
    this.dashboardService.resetToDefault(true).subscribe({
      next: (dashboardConfig: DashboardConfigDTO | null) => {
        
        // If we received a dashboard config, update our dashboard
        if (dashboardConfig && dashboardConfig.widgets) {
          // Convert dashboard config to widget data
          const widgets = dashboardConfig.widgets.map((widgetConfig: DashboardWidgetConfigDTO) => {
            return {
              id: widgetConfig.id,
              type: widgetConfig.widgetType,
              title: widgetConfig.title,
              dataSource: widgetConfig.dataSource || this.getDataSourceIdForWidgetType(widgetConfig.widgetType),
              position: {
                x: widgetConfig.position?.x || 0,
                y: widgetConfig.position?.y || 0,
                cols: widgetConfig.position?.cols || 3,
                rows: widgetConfig.position?.rows || 3,
                id: widgetConfig.id
              },
              cols: widgetConfig.position?.cols || 3,
              rows: widgetConfig.position?.rows || 3,
              defaultFilters: widgetConfig.defaultFilters || {},
              settings: {
                category: widgetConfig.widgetType,
                filters: widgetConfig.defaultFilters || {},
                fields: ['name', 'path', 'registeredPercentage', 'percentComplete', 'station'],
                displayType: 'table'
              },
              isLoading: false,
              data: widgetConfig.widgetType === 'userProgress' ?
                { users: [], totalCount: 0, filters: {} } :
                { items: [] }
            };
          });
          
          this.dashboard = widgets;
          
          // Force change detection
          this.cdr.detectChanges();
          
          // Load widget data
          this.loadWidgetsWithDelay();
        } else {
          // If we didn't receive a dashboard config, create a default one manually
          this.resetDashboardManually(true, false);
        }
      },
      error: (error: Error) => {
        console.error('[DASHBOARD-COMPONENT] Error resetting dashboard:', error);
        // On error, create a default dashboard manually
        this.resetDashboardManually(true, false);
      }
    });
  }
  
  /**
   * Fallback method to reset dashboard if loading from layout fails
   * @param saveImmediately Whether to save the dashboard immediately after creation
   * @param forceSaveToApi Whether to force save to API (defaults to false)
   */
  public resetDashboardManually(saveImmediately: boolean = true, forceSaveToApi: boolean = false): void {
    const timestamp1 = Date.now();
    
    this.dashboard = [
      {
        id: `widget-userProgress-${timestamp1}`,
        type: 'userProgress',
        title: 'Bruger fremgang - Oversigt',
        dataSource: this.getDataSourceIdForWidgetType('userProgress'),
        position: { x: 0, y: 0, cols: 6, rows: 4, id: `widget-userProgress-${timestamp1}` },
        x: 0, y: 0, cols: 6, rows: 4,
        settings: {
          category: 'userProgress',
          filters: { 
            group: 'all',
            sortBy: 'registeredPercentage',
            sortDirection: 'desc' 
          },
          fields: ['name', 'path', 'registeredPercentage', 'percentComplete', 'station'],
          displayType: 'table'
        },
        defaultFilters: { 
          group: 'all',
          sortBy: 'registeredPercentage',
          sortDirection: 'desc' 
        },
        data: { users: [] }, // Initialize with empty data structure to prevent UI errors
        isLoading: false // IMPORTANT: Start with isLoading false
      }
    ];
    
    // Force change detection to immediately render the widgets
    this.cdr.detectChanges();
    
    if (saveImmediately) {
      // Only force API save if explicitly requested, otherwise just save to local storage
      this.dashboardAdapter.saveWidgets(this.dashboard, forceSaveToApi).subscribe();
    }
    
    // Schedule loading data for these widgets with a small delay
    setTimeout(() => {
      if (!this.isDestroyed) {
        this.loadWidgetsWithDelay();
      }
    }, 300);
  }

  // Load widget data
  public loadWidgetData(widget: WidgetData): void {
    if (!widget) return;

    if (widget.type === 'eventsCalendar') {
      widget.isLoading = false;
      widget.eventsCalendarReloadToken = (widget.eventsCalendarReloadToken || 0) + 1;
      this.cdr.detectChanges();
      return;
    }
    
    // Set loading state - but only set isLoading if this is initial load (not appending)
    // If currentPage > 1, we're loading more data, so only isLoadingMore should be true
    const isAppending = (widget.currentPage ?? 1) > 1;
    if (!isAppending) {
      widget.isLoading = true;
      if (widget.type === 'userProgress' || widget.type === 'locationProgress') {
        widget.initialLoadComplete = false;
      }
    }
    this.cdr.detectChanges();

    const progressListWidget =
      widget.type === 'userProgress' || widget.type === 'locationProgress';
    const loadingTimeoutMs = progressListWidget ? 120000 : 10000;

    if (progressListWidget && widget.initialLoadComplete === false) {
      widget.currentPage = 1;
      widget.hasMoreData = true;
      this.updateProgressListLoadingMore(widget, false);
      this.scrollLoadingMap.set(widget.id, false);
    }

    const loadingTimeout = setTimeout(() => {
      if (widget.isLoading || widget.isLoadingMore) {
        console.warn(`[DASHBOARD-COMPONENT] Loading timeout for widget: ${widget.type} - resetting loading state`);
        if (!progressListWidget) {
          widget.isLoading = false;
          this.updateProgressListLoadingMore(widget, false);
          this.scrollLoadingMap.set(widget.id, false);
          this.cdr.detectChanges();
        }
      }
    }, loadingTimeoutMs);
    
    // Get filters from widget settings, with fallback to defaultFilters
    let filters = { ...(widget.settings?.filters || {}) };
    
    // If no filters are set but defaultFilters exist, use defaultFilters
    if (Object.keys(filters).length === 0 && widget.defaultFilters) {
      filters = { ...widget.defaultFilters };
      
      // Also update the widget settings to persist the default filters
      if (!widget.settings) {
        widget.settings = {
          category: widget.type,
          filters: { ...filters },
          fields: ['name', 'path', 'registeredPercentage', 'percentComplete', 'station'],
          displayType: 'table'
        };
      } else {
        widget.settings.filters = { ...filters };
      }
    }
    
    // Initialize pagination state if not already set (for initial load, not appending)
    // Only reset if this is a fresh load (not loading more)
    if (widget.currentPage === undefined || widget.currentPage === null) {
      widget.currentPage = 1;
      widget.totalPages = 1;
      widget.hasMoreData = true;
      this.updateProgressListLoadingMore(widget, false);
    }
    
    const dataSource = widget.dataSource || this.getDataSourceIdForWidgetType(widget.type);
    
    // Debug logging for filters
    
    // Handle userProgress and userProgressStats widgets
    if (widget.type === 'userProgress' || widget.type === 'userProgressStats') {
      
      const pageSize = this.getProgressListPageSize(widget);
      
      // Determine if we're loading more (appending) or initial load
      const isAppending = (widget.currentPage ?? 1) > 1;
      const currentPage = widget.currentPage || 1;
      
      const listRequest = this.buildProgressListRequest(widget, filters, currentPage, pageSize);
      
      // Set loading more state if appending
      if (isAppending) {
        this.updateProgressListLoadingMore(widget, true);
      }
      
      // Get user progress data
      getUserProgressData(this.http, listRequest).subscribe({
        next: (response: ListResponseDTO) => {
          
          // Clear the timeout since we got a response
          clearTimeout(loadingTimeout);
          
          let lastBatchCount = response.data?.length ?? 0;
          
          // Update widget data - append if loading more, replace if initial load
          // Use immutable update pattern to trigger change detection properly
          if (isAppending && widget.data?.users) {
            // Append new users to existing array without replacing the entire array reference
            const existingUsers = widget.data.users;
            const responseUsers = response.data || [];
            const existingCount = existingUsers.length;
            const pageSize = listRequest.pageSize || 10;
            
            // Defensive: If API returns all items from page 1 to current page, slice to get only new items
            // Expected new items count should be pageSize (15), so if we get more, take only the last pageSize items
            let newUsers: any[];
            if (responseUsers.length > pageSize && currentPage > 1) {
              // API might have returned all items, take only the last pageSize items
              newUsers = responseUsers.slice(-pageSize);
              console.warn(`[DASHBOARD-COMPONENT] API returned ${responseUsers.length} users (expected ${pageSize} for page ${currentPage}), taking last ${pageSize} items`);
            } else {
              newUsers = responseUsers;
            }
            
            // Create a Set of existing user IDs for fast lookup
            const existingUserIds = new Set(existingUsers.map((u: any) => u.id).filter(Boolean));
            
            // Filter out any users that already exist (in case API returns duplicates)
            const uniqueNewUsers = newUsers.filter((user: any) => !existingUserIds.has(user.id));
            lastBatchCount = uniqueNewUsers.length;
            
            
            // Only append if we actually got new data
            if (uniqueNewUsers.length > 0) {
              // IMPORTANT: Push items to existing array instead of creating new array
              // This preserves scroll position naturally without needing restoration
              existingUsers.push(...uniqueNewUsers);
              
              // Update metadata without replacing the users array reference
              if (widget.data) {
                widget.data.totalCount = response.meta?.totalItems || 0;
                widget.data.filters = filters;
              }
              
            } else {
              console.warn(`[DASHBOARD-COMPONENT] No new users to append, keeping existing ${existingCount} users`);
              widget.currentPage = response.meta?.page || currentPage;
              widget.totalPages = response.meta?.totalPages || 1;
              widget.hasMoreData = false;
              widget.isLoading = false;
              this.updateProgressListLoadingMore(widget, false);
              widget.initialLoadComplete = true;
              this.scrollLoadingMap.set(widget.id, false);
              this.cdr.detectChanges();
              return; // Exit early if no new data
            }
          } else {
            // Initial load - replace all data
            widget.data = {
              ...widget.data,
              users: response.data || [],
              totalCount: response.meta?.totalItems || 0,
              filters: filters
            };
          }
          
          // Update pagination state
          widget.currentPage = response.meta?.page || currentPage;
          widget.totalPages = response.meta?.totalPages || 1;
          const appendedCount = lastBatchCount;
          widget.hasMoreData = this.computeProgressListHasMoreData(
            widget,
            response,
            listRequest,
            isAppending,
            appendedCount
          );

          this.syncWidgetLocationFilterCache(widget, response);
          
          // Create backup of original data to preserve filter options (only if not already backed up)
          if (!widget.originalData && response.data && response.data.length > 0 && !isAppending) {
            widget.originalData = { ...widget.data };
          }
          
          // Set loading state
          widget.isLoading = false;
          this.updateProgressListLoadingMore(widget, false);
          widget.initialLoadComplete = true;
          this.scrollLoadingMap.set(widget.id, false);
          
          // Trigger change detection
          if (isAppending) {
            // Mark for check instead of forcing change detection to avoid full re-render
            // Since we mutated the array in place, Angular will only render the new items
            this.cdr.markForCheck();
            this.restoreScrollPosition(widget.id, 'user-list');
          } else {
            // For initial load, use normal change detection
            this.cdr.detectChanges();
          }
        },
        error: (error: HttpErrorResponse) => {
          console.error(`[DASHBOARD-COMPONENT] Error loading user progress data for widget: ${widget.type}`, error);
          
          // Clear the timeout since we got an error response
          clearTimeout(loadingTimeout);
          
          // Set loading state
          widget.isLoading = false;
          this.updateProgressListLoadingMore(widget, false);
          widget.initialLoadComplete = true;
          
          // Force change detection even on error to update UI
          this.cdr.detectChanges();
        }
      });
    } else if (widget.type === 'locationProgress') {
      
      const pageSize = this.getProgressListPageSize(widget);
      
      // Determine if we're loading more (appending) or initial load
      const isAppending = (widget.currentPage ?? 1) > 1;
      const currentPage = widget.currentPage || 1;
      
      // Create list request for location progress
      const listRequest = this.buildProgressListRequest(widget, filters, currentPage, pageSize);
      
      
      // Set loading more state if appending
      if (isAppending) {
        this.updateProgressListLoadingMore(widget, true);
      }
      
      // Get location progress data
      getLocationProgressData(this.componentDataSourceService, listRequest).subscribe({
        next: (response: ListResponseDTO) => {
          
          // Clear the timeout since we got a response
          clearTimeout(loadingTimeout);
          
          let lastBatchCount = response.data?.length ?? 0;
          
          // Update widget data - append if loading more, replace if initial load
          // Use immutable update pattern to trigger change detection properly
          if (isAppending && widget.data?.locations) {
            // Append new locations to existing array without replacing the entire array reference
            const existingLocations = widget.data.locations;
            const responseLocations = response.data || [];
            const existingCount = existingLocations.length;
            const pageSize = listRequest.pageSize || 10;
            
            // Defensive: If API returns all items from page 1 to current page, slice to get only new items
            // Expected new items count should be pageSize (15), so if we get more, take only the last pageSize items
            let newLocations: any[];
            if (responseLocations.length > pageSize && currentPage > 1) {
              // API might have returned all items, take only the last pageSize items
              newLocations = responseLocations.slice(-pageSize);
              console.warn(`[DASHBOARD-COMPONENT] API returned ${responseLocations.length} locations (expected ${pageSize} for page ${currentPage}), taking last ${pageSize} items`);
            } else {
              newLocations = responseLocations;
            }
            
            // Create a Set of existing location IDs for fast lookup
            const existingLocationIds = new Set(existingLocations.map((l: any) => l.id).filter(Boolean));
            
            // Filter out any locations that already exist (in case API returns duplicates)
            const uniqueNewLocations = newLocations.filter((location: any) => !existingLocationIds.has(location.id));
            lastBatchCount = uniqueNewLocations.length;
            
            
            // Only append if we actually got new data
            if (uniqueNewLocations.length > 0) {
              // IMPORTANT: Push items to existing array instead of creating new array
              // This preserves scroll position naturally without needing restoration
              existingLocations.push(...uniqueNewLocations);
              
              // Update metadata without replacing the locations array reference
              if (widget.data) {
                widget.data.totalCount = response.meta?.totalItems || 0;
                widget.data.filters = filters;
              }
              
            } else {
              console.warn(`[DASHBOARD-COMPONENT] No new locations to append, keeping existing ${existingCount} locations`);
              widget.currentPage = response.meta?.page || currentPage;
              widget.totalPages = response.meta?.totalPages || 1;
              widget.hasMoreData = false;
              widget.isLoading = false;
              this.updateProgressListLoadingMore(widget, false);
              widget.initialLoadComplete = true;
              this.scrollLoadingMap.set(widget.id, false);
              this.cdr.detectChanges();
              return; // Exit early if no new data
            }
          } else {
            // Initial load - replace all data
            widget.data = {
              ...widget.data,
              locations: response.data || [],
              totalCount: response.meta?.totalItems || 0,
              filters: filters
            };
          }
          
          // Update pagination state
          widget.currentPage = response.meta?.page || currentPage;
          widget.totalPages = response.meta?.totalPages || 1;
          widget.hasMoreData = this.computeProgressListHasMoreData(
            widget,
            response,
            listRequest,
            isAppending,
            lastBatchCount
          );
          
          // Create backup of original data to preserve filter options (only if not already backed up)
          if (!widget.originalData && response.data && response.data.length > 0 && !isAppending) {
            widget.originalData = { ...widget.data };
          }
          
          // Set loading state
          widget.isLoading = false;
          this.updateProgressListLoadingMore(widget, false);
          widget.initialLoadComplete = true;
          this.scrollLoadingMap.set(widget.id, false);
          
          // Trigger change detection
          if (isAppending) {
            // Mark for check instead of forcing change detection to avoid full re-render
            // Since we mutated the array in place, Angular will only render the new items
            this.cdr.markForCheck();
            this.restoreScrollPosition(widget.id, 'location-list');
          } else {
            // For initial load, use normal change detection
            this.cdr.detectChanges();
          }
        },
        error: (error: HttpErrorResponse) => {
          console.error(`[DASHBOARD-COMPONENT] Error loading location progress data for widget: ${widget.type}`, error);
          
          // Clear the timeout since we got an error response
          clearTimeout(loadingTimeout);
          
          // Set loading state
          widget.isLoading = false;
          this.updateProgressListLoadingMore(widget, false);
          widget.initialLoadComplete = true;
          this.scrollLoadingMap.set(widget.id, false);
          
          // Force change detection even on error to update UI
          this.cdr.detectChanges();
        }
      });
    } else if (widget.type === 'cateringSummary') {
      const daysRaw = filters['days'];
      const days = normalizeCateringSummaryDays(Number(daysRaw));
      const groupRaw = filters['catering_group'];
      const cateringGroupId = groupRaw != null && groupRaw !== '' ? Number(groupRaw) : null;
      const safeGroupId = cateringGroupId != null && !Number.isNaN(cateringGroupId) && cateringGroupId > 0
        ? cateringGroupId
        : null;

      getCateringSummaryData(this.http, days, safeGroupId).subscribe({
        next: (summary) => {
          clearTimeout(loadingTimeout);
          widget.data = summary;
          widget.isLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          clearTimeout(loadingTimeout);
          widget.data = { period: { days, start: '', end: '' }, totals: { preferences: {}, extras: [] }, days: [] };
          widget.isLoading = false;
          this.cdr.detectChanges();
        }
      });
    } else if (widget.type === 'simpleNumber') {
      
      const aggregateSources = new Set([
        'completion_rate',
        'completed_users',
        'users_with_warnings',
      ]);
      const usesTotalItemsOnly = dataSource === 'simple_number' || dataSource === 'user_count';
      const listPageSize = usesTotalItemsOnly
        ? 1000
        : (aggregateSources.has(dataSource) ? 10000 : 1);

      const listRequest: ListRequestDTO = createListRequest({
        page: 1,
        pageSize: listPageSize,
        sortBy: filters['sortBy'],
        sortDir: filters['sortDir'] as 'asc' | 'desc',
        filters: filters,
        search: filters['search'],
        includeFilters: true
      });
      
      // Choose the appropriate data function based on data source
      let dataFunction: (http: HttpClient, request?: ListRequestDTO) => Observable<ListResponseDTO>;
      
      switch (dataSource) {
        case 'user_count':
          dataFunction = getUserCountData;
          break;
        case 'completion_rate':
          dataFunction = getCompletionRateData;
          break;
        case 'completed_users':
          dataFunction = getCompletedUsersCount;
          break;
        case 'users_with_warnings':
          dataFunction = getUsersWithWarningsCount;
          break;
        case 'registered_to_progress_ratio':
          dataFunction = getRegisteredToProgressRatio;
          break;
        case 'simple_number':
        default:
          dataFunction = getSimpleNumberData;
          break;
      }
      
      // Get simple number data using the selected function
      dataFunction(this.http, listRequest).subscribe({
        next: (response: ListResponseDTO) => {
          
          // Clear the timeout since we got a response
          clearTimeout(loadingTimeout);
          
          // Update widget data - simple number widgets expect a single data object
          const numberData = response.data && response.data.length > 0 ? response.data[0] : { value: 0, title: 'No Data' };
          widget.data = numberData;
          
          // Set loading state
          widget.isLoading = false;
          
          // Force change detection to ensure UI updates properly
          this.cdr.detectChanges();
          
          // Additional change detection with short delay to handle OnPush edge cases
          setTimeout(() => {
            if (!this.isDestroyed) {
              this.cdr.detectChanges();
            }
          }, 50);
        },
        error: (error: HttpErrorResponse) => {
          console.error(`[DASHBOARD-COMPONENT] Error loading simple number data for widget: ${widget.type}`, error);
          
          // Clear the timeout since we got an error response
          clearTimeout(loadingTimeout);
          
          // Set default data on error
          widget.data = { value: 0, title: 'Error loading data' };
          
          // Set loading state
          widget.isLoading = false;
          
          // Force change detection even on error to update UI
          this.cdr.detectChanges();
        }
      });
    } else {
      // For other widget types - use componentDataSourceService with ListRequestDTO
      
      // Create a proper ListRequestDTO with all required parameters
      const listRequest: ListRequestDTO = createListRequest({
        page: 1,
        pageSize: 100,
        sortBy: filters['sortBy'],
        sortDir: filters['sortDirection'] as 'asc' | 'desc',
        filters: filters,
        search: filters['search'],
        includeFilters: true
      });
      

      // Add sourceType for compatibility with DashboardDataRequestDTO
      const dashboardRequest = {
        ...listRequest,
        sourceType: dataSource
      };

      // Use componentDataSourceService to get data
      this.componentDataSourceService.fetchDashboardData(dataSource, dashboardRequest).subscribe({
        next: (response: ListResponseDTO) => {
          
          // Clear the timeout since we got a response
          clearTimeout(loadingTimeout);
          
          // Update widget data
          widget.data = {
            items: response.data || [],
            totalCount: response.meta?.totalItems || 0,
            filters: filters
          };
          
          // Create backup of original data to preserve filter options (only if not already backed up)
          if (!widget.originalData && response.data && response.data.length > 0) {
            widget.originalData = { ...widget.data };
          }
          
          // Set loading state
          widget.isLoading = false;
          
          // Force change detection to ensure UI updates properly
          this.cdr.detectChanges();
          
          // Additional change detection with short delay to handle OnPush edge cases
          setTimeout(() => {
            if (!this.isDestroyed) {
              this.cdr.detectChanges();
            }
          }, 50);
        },
        error: (error: HttpErrorResponse) => {
          console.error(`[DASHBOARD-COMPONENT] Error loading data for widget: ${widget.type}`, error);
          
          // Clear the timeout since we got an error response
          clearTimeout(loadingTimeout);
          
          // Set loading state
          widget.isLoading = false;
          
          // Force change detection even on error to update UI
          this.cdr.detectChanges();
        }
      });
    }
  }

  // Create mock users to avoid empty state
  private createMockUsers(count: number): any[] {
    const stations = ['Station Nord', 'Station Syd', 'Station Øst', 'Station Vest'];
    const paths = ['Indsatsleder', 'Holdleder', 'FUI'];
    
    return Array(count).fill(0).map((_, index) => ({
      id: index + 1,
      name: `Demo Bruger ${index + 1}`,
      station: stations[Math.floor(Math.random() * stations.length)],
      path: paths[Math.floor(Math.random() * paths.length)],
      status: Math.random() > 0.5 ? 'ok' : 'warning',
      registeredPercentage: Math.floor(Math.random() * 100),
      totalPaths: Math.floor(Math.random() * 3) + 1,
      completedPaths: Math.floor(Math.random() * 2),
      inProgressPaths: Math.floor(Math.random() * 2),
      notStartedPaths: 0
    }));
  }

  // --- TEMPLATE-REFERENCED METHODS (SAFE STUBS/TRIVIAL IMPLEMENTATIONS) ---

  public toggleField(field: string): void {
    if (!this.selectedWidget?.settings?.fields) return;
    
    const fields = this.selectedWidget.settings.fields;
    const index = fields.indexOf(field);
    
    if (index === -1) {
      fields.push(field);
    } else {
      fields.splice(index, 1);
    }
    
    this.cdr.detectChanges();
  }

  public updateWidgetSettings(widgetId: string, settings: any): void {
    const widget = this.dashboard.find(w => w.id === widgetId);
    if (widget) {
      widget.settings = { ...widget.settings, ...settings };
      this.cdr.detectChanges();
    }
  }

  public isLocationInPopularList(location: string, users: any[]): boolean {
    if (!location || !Array.isArray(users)) return false;
    const locationCounts = users.reduce((acc, user) => {
      const loc = user.station;
      acc[loc] = (acc[loc] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return locationCounts[location] > (users.length * 0.1); // More than 10% of users
  }

  public applyFilter(widget: any, key: string, value: any): void {
    if (!widget.settings) return;
    widget.settings.filters = widget.settings.filters || {};
    widget.settings.filters[key] = value;
    this.cdr.detectChanges();
  }

  public openLocationSelectionDialog(widget: any): void {    
    if (!this.dialog) return;
    const widgetRef = widget;

    this.openSelectionDialogAfterFilterClose(() => {
      const cachedLocations = this.allWidgetLocations.get(widgetRef.id);
      const locations: string[] = cachedLocations && cachedLocations.length > 0
        ? cachedLocations
        : ([...new Set([
            ...this.getUniqueValues(widgetRef.data?.users || [], 'station'),
            ...this.getUniqueValues(widgetRef.originalData?.users || [], 'station')
          ])].filter(Boolean) as string[]).sort();

      const existingStations = this.getSelectedStationsFromFilters(widgetRef.settings?.filters);
      const dialogRef = this.dialog.open(LocationMultiSelectionDialogComponent, {
        ...this.selectionDialogConfig,
        data: {
          selectedStations: existingStations,
          locations
        }
      });

      dialogRef.afterClosed().subscribe((result: string[] | undefined) => {
        if (result === undefined) return;
        this.applyStationsFilter(widgetRef, result);
      });
    });
  }

  public getSelectedStationsFromFilters(filters: Record<string, unknown> | undefined): string[] {
    if (!filters) return [];
    const stations = filters['stations'];
    if (Array.isArray(stations)) {
      return stations.map((s) => String(s)).filter((s) => s && s !== 'all');
    }
    const single = filters['station'];
    if (typeof single === 'string' && single && single !== 'all') {
      return [single];
    }
    return [];
  }

  public hasStationFilters(filters: Record<string, unknown> | undefined): boolean {
    return this.getSelectedStationsFromFilters(filters).length > 0;
  }

  public isStationChipActive(filters: Record<string, unknown> | undefined, loc: string): boolean {
    return this.getSelectedStationsFromFilters(filters).includes(loc);
  }

  public toggleStationFilter(widget: WidgetData, loc: string): void {
    const w = this.resolveDashboardWidget(widget);
    const selected = this.getSelectedStationsFromFilters(w.settings?.filters);
    const trimmed = String(loc).trim();
    if (!trimmed || trimmed === 'all') {
      return;
    }
    const next = selected.includes(trimmed)
      ? selected.filter((s) => s !== trimmed)
      : [...selected, trimmed];
    this.applyStationsFilter(w, next);
  }

  public applyStationsFilter(widget: WidgetData, stations: string[]): void {
    const w = this.resolveDashboardWidget(widget);
    if (!w.settings) return;
    w.settings.filters = w.settings.filters || {};
    const cleaned = stations.map((s) => String(s).trim()).filter((s) => s && s !== 'all');
    if (cleaned.length === 0) {
      delete w.settings.filters['stations'];
      delete w.settings.filters['station'];
    } else {
      w.settings.filters['stations'] = cleaned;
      delete w.settings.filters['station'];
    }
    w.currentPage = 1;
    w.hasMoreData = true;
    this.updateProgressListLoadingMore(w, false);
    this.loadWidgetData(w);
    this.cdr.detectChanges();
    if (this.selectedWidget && this.selectedWidget.id === w.id) {
      this.selectedWidget = w;
    }
  }

  public clearStationFilters(widget: WidgetData): void {
    this.applyStationsFilter(widget, []);
  }
  
  public openEducationSelectionDialog(widget: any): void {
    if (!this.dialog) return;
    const widgetRef = widget;

    this.openSelectionDialogAfterFilterClose(() => {
      this.fetchEducationalPathsList().subscribe({
        next: (paths) => {
          let educations = this.mapEducationalPathList(paths);
          if (educations.length === 0) {
            educations = this.getUniquePathObjects(widgetRef.originalData?.users || widgetRef.data?.users || []);
          } else {
            this.educationalPaths = educations;
            this.educationalPathsLoaded = true;
            this.cdr.detectChanges();
          }
          this.openEducationSelectionDialogWithData(widgetRef, educations);
        },
        error: () => {
          const fallback = this.educationalPaths.length > 0
            ? this.educationalPaths
            : this.getUniquePathObjects(widgetRef.originalData?.users || widgetRef.data?.users || widgetRef.data?.locations || []);
          this.openEducationSelectionDialogWithData(widgetRef, fallback);
        }
      });
    });
  }

  private openEducationSelectionDialogWithData(widget: any, educations: { id: string | number; name: string }[]): void {
    const widgetRef = this.resolveDashboardWidget(widget);
    const dialogRef = this.dialog.open(EducationSelectionDialogComponent, {
      ...this.selectionDialogConfig,
      data: {
        selectedEducation: widgetRef.settings?.filters?.['educationalPathId'] || 'all',
        educations
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        this.applyFilterAndLoad(widgetRef, 'educationalPathId', result === 'all' ? '' : result);
      }
    });
  }

  public openGroupSelectionDialog(widget: any): void {
    if (!this.dialog) return;
    const widgetRef = widget;

    this.openSelectionDialogAfterFilterClose(() => {
      this.fetchUserGroupsList().subscribe({
        next: (groups) => {
          if (groups.length > 0) {
            this.userGroups = groups;
            this.userGroupsLoaded = true;
            this.cdr.detectChanges();
          }
          this.openGroupSelectionDialogWithData(widgetRef, groups.length > 0 ? groups : this.userGroups);
        },
        error: () => {
          this.openGroupSelectionDialogWithData(widgetRef, this.userGroups);
        }
      });
    });
  }

  private openGroupSelectionDialogWithData(widget: any, groups: { id: number; name: string }[]): void {
    const widgetRef = this.resolveDashboardWidget(widget);
    const dialogRef = this.dialog.open(GroupSelectionDialogComponent, {
      ...this.selectionDialogConfig,
      data: {
        selectedGroupId: widgetRef.settings?.filters?.['groupId'] ?? 'all',
        groups
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        this.applyFilterAndLoad(widgetRef, 'groupId', result === 'all' ? '' : result);
      }
    });
  }

  public getCateringGroupName(groupId: number | string | null | undefined): string {
    if (groupId == null || groupId === '') {
      return '';
    }
    const numericId = Number(groupId);
    const match = this.cateringGroupFilterOptions.find((opt) => opt.value === numericId);
    return match?.label || String(groupId);
  }

  public navigateToEvent(guid: string): void {
    if (!guid) return;
    const url = this.router.createUrlTree(['/event', guid]);
    window.open(url.toString(), '_blank');
  }

  public openCateringPreferences(event: CateringSummaryEvent): void {
    if (!event?.guid) return;
    const row: EventCateringApprovalListItemDTO = {
      event_id: event.event_id,
      guid: event.guid,
      event_type: 'exercise',
      start_date: event.start_date || '',
      end_date: event.end_date || '',
      event_name: event.event_name,
      location: event.location,
      current_pending_user_count: event.current_pending_user_count ?? event.participant_count,
      approved_pending_user_count: event.approved_pending_user_count ?? null,
      approved_at: event.approved_at ?? null,
      approval_status: event.approval_status || 'pending'
    };
    this.dialog.open(DunaCateringPreferencesDialogComponent, {
      ...DUNA_CATERING_PREFERENCES_DIALOG_CONFIG,
      panelClass: 'centered-dialog',
      data: { row }
    });
  }

  public getUniqueValues(arr: any[], key: string): any[] {
    if (!Array.isArray(arr)) return [];
    return [...new Set(arr.map(item => item[key]).filter(Boolean))];
  }

  public getUniquePathObjects(users: any[]): {id: string, name: string}[] {
    if (!Array.isArray(users)) return [];
    const seen = new Set<string>();
    const result: {id: string, name: string}[] = [];
    for (const user of users) {
      if (user.paths && Array.isArray(user.paths)) {
        for (const p of user.paths) {
          const id = p.id || p.pathId || '';
          const name = (p.name && p.name.trim()) ? p.name : (p.full_name || id);
          if (id && !seen.has(id)) { seen.add(id); result.push({ id, name }); }
        }
      } else if (user.pathId && !seen.has(user.pathId)) {
        seen.add(user.pathId);
        result.push({ id: user.pathId, name: user.path || user.pathId });
      }
    }
    return result.sort((a, b) => a.name.localeCompare(b.name));
  }

  

  

  public sortUserProgressData(widget: any, key: string): void {
    widget = this.resolveDashboardWidget(widget);
    if (!widget.settings?.filters) return;
    const currentSortBy = widget.settings.filters['sortBy'];
    const currentDirection = widget.settings.filters['sortDir'];
    
    let newDirection = 'asc';
    if (currentSortBy === key) {
      newDirection = currentDirection === 'asc' ? 'desc' : 'asc';
    }
    
    widget.settings.filters['sortBy'] = key;
    widget.settings.filters['sortDir'] = newDirection;
    
    // Remove legacy key if present
    if (widget.settings.filters['sortDirection']) {
      delete widget.settings.filters['sortDirection'];
    }
    
    widget.currentPage = 1;
    widget.hasMoreData = true;
    this.updateProgressListLoadingMore(widget, false);
    
    this.cdr.detectChanges();
    this.loadWidgetData(widget);
  }

  public sortLocationProgressData(widget: any, key: string): void {
    widget = this.resolveDashboardWidget(widget);
    if (!widget.settings?.filters) return;
    const currentSortBy = widget.settings.filters['sortBy'];
    const currentDirection = widget.settings.filters['sortDir'] || widget.settings.filters['sortDirection']; // Support both for backward compatibility
    
    // Update filters with new sort information
    let newDirection = 'asc';
    if (currentSortBy === key) {
      // Toggle direction if same field
      newDirection = currentDirection === 'asc' ? 'desc' : 'asc';
    }
    
    
    // Update the widget settings immediately for UI feedback
    widget.settings.filters['sortBy'] = key;
    widget.settings.filters['sortDir'] = newDirection; // Use sortDir to match backend expectations
    
    // Remove old sortDirection if it exists to avoid confusion
    if (widget.settings.filters['sortDirection']) {
      delete widget.settings.filters['sortDirection'];
    }
    
    
    // Force change detection so UI shows sort direction immediately
    this.cdr.detectChanges();
    
    // Reset pagination when sorting changes
    widget.currentPage = 1;
    widget.hasMoreData = true;
    this.updateProgressListLoadingMore(widget, false);
    
    // Immediately load widget data to apply sort on the backend
    this.loadWidgetData(widget);
  }

  public closeFilterDialog(): void {
    if (this.filterOverlayRef) {
      this.filterOverlayRef.dispose();
      this.filterOverlayRef = null;
    }
    this.isFilterOpen = false;
  }

  public applyFilterAndCloseDialog(): void {
    if (this.selectedWidget) {
      // Reset pagination when filters change
      this.selectedWidget.currentPage = 1;
      this.selectedWidget.hasMoreData = true;
      this.updateProgressListLoadingMore(this.selectedWidget, false);
      this.loadWidgetData(this.selectedWidget);
    }
    this.closeFilterDialog();
  }

  public getLayoutPreviewWidgets(layout: any): any[] {
    return layout.widgets || [];
  }

  public applyLayoutAndClose(layoutId: string): void {
    this.dashboardService.fetchCurrentDashboardDirectly().pipe(
      take(1)
    ).subscribe(config => {
      if (config) {
        this.dashboard = config.widgets.map(w => ({
          id: w.id,
          type: w.widgetType,
          title: w.title,
          position: {
            x: w.position?.x || 0,
            y: w.position?.y || 0,
            cols: w.position?.cols || 6,
            rows: w.position?.rows || 4,
            id: w.id
          },
          x: w.position?.x || 0,
          y: w.position?.y || 0,
          cols: w.position?.cols || 6,
          rows: w.position?.rows || 4,
          data: {},
          isLoading: false,
          settings: {
            category: w.widgetType,
            filters: {},
            fields: [],
            displayType: 'table'
          }
        } as WidgetData));
        this.cdr.detectChanges();
      }
    });
    this.isLayoutSelectorOpen = false;
  }

  // --- RE-ADDED METHODS TO FIX LINTER ERRORS ---

  private hydrateDashboardFromMemoryOrCache(): void {
    if (this.isPublicShareView || this.isShareLinkAdminFiltersView) {
      return;
    }

    if (DunaDashboardComponent.widgetDataLoaded && DunaDashboardComponent.dashboardData.length > 0) {
      this.dashboard = [...DunaDashboardComponent.dashboardData];
      this.initializing = false;
      this.loadDashboardComplete = true;
      this.showEmptyState = false;
      this.cdr.markForCheck();
      this.scheduleGridsterLayout();
      setTimeout(() => {
        if (!this.isDestroyed) {
          this.loadWidgetsWithDelay();
        }
      }, 0);
      return;
    }

    const cachedConfig = this.dashboardService.peekCurrentDashboardConfig();
    if (cachedConfig?.widgets?.length && this.dashboard.length === 0) {
      this.applyDashboardFromConfig(cachedConfig);
      return;
    }

    this.subscription.add(
      this.dashboardService.getCurrentDashboardConfig().pipe(
        filter((config): config is DashboardConfigDTO => !!config?.widgets?.length),
        take(1)
      ).subscribe((config) => {
        if (this.isDestroyed || this.dashboard.length > 0) {
          return;
        }
        this.applyDashboardFromConfig(config);
      })
    );
  }

  private ensureServerDashboardLoad(force = false): void {
    if (this.isPublicShareView || this.isShareLinkAdminFiltersView || this.isDestroyed) {
      return;
    }
    if (!force && this.dashboard.length > 0) {
      this.initializing = false;
      return;
    }
    if (this.dashboardServerFetchInFlight && !force) {
      return;
    }

    if (!this.options) {
      this.initializeGridsterOptions();
    }

    this.dashboardServerFetchInFlight = true;
    this.dashboardService.fetchCurrentDashboardDirectly().pipe(take(1)).subscribe({
      next: (config) => {
        this.dashboardServerFetchInFlight = false;
        if (this.isDestroyed) {
          return;
        }
        if (config?.widgets?.length && this.dashboard.length === 0) {
          this.applyDashboardFromConfig(config);
        }
      },
      error: () => {
        this.dashboardServerFetchInFlight = false;
      }
    });
  }

  public loadUserDashboardConfig(userId: string, retryCount: number = 0, maxRetries: number = 5): void {
    this.currentUserId = userId;

    if (!this.options) {
      this.initializeGridsterOptions();
    }

    if (this.dashboard.length > 0) {
      this.initializing = false;
      this.cdr.markForCheck();
      return;
    }

    const delay = retryCount > 0 ? 2000 : 100;
    setTimeout(() => {
      if (this.isDestroyed || this.dashboard.length > 0) {
        return;
      }

      this.dashboardService.fetchCurrentDashboardDirectly()
        .pipe(take(1))
        .subscribe({
          next: (config) => {
            if (this.isDestroyed) {
              return;
            }

            if (config?.widgets?.length) {
              this.applyDashboardFromConfig(config);
              return;
            }

            if (retryCount < maxRetries) {
              console.warn(`⚠️ [DASHBOARD-COMPONENT] No dashboard config or widgets found (attempt ${retryCount + 1}), retrying in 2 seconds...`);
              setTimeout(() => {
                this.loadUserDashboardConfig(userId, retryCount + 1, maxRetries);
              }, 2000);
            } else {
              console.error(`❌ [DASHBOARD-COMPONENT] No dashboard config found after ${maxRetries + 1} attempts - showing empty state`);
              this.dashboard = [];
              this.showEmptyState = true;
              this.loadDashboardComplete = true;
              this.initializing = false;
              this.cdr.detectChanges();
            }
          },
          error: (err) => {
            console.error(`❌ [DASHBOARD-COMPONENT] Error loading user dashboard (attempt ${retryCount + 1}):`, err);

            if (retryCount < maxRetries) {
              console.warn(`⚠️ [DASHBOARD-COMPONENT] Retrying dashboard load in 2 seconds... (${retryCount + 1}/${maxRetries})`);
              setTimeout(() => {
                this.loadUserDashboardConfig(userId, retryCount + 1, maxRetries);
              }, 2000);
            } else {
              console.error(`❌ [DASHBOARD-COMPONENT] Failed to load dashboard after ${maxRetries + 1} attempts - showing empty state`);
              this.dashboard = [];
              this.showEmptyState = true;
              this.loadDashboardComplete = true;
              this.initializing = false;
              this.cdr.detectChanges();
            }
          }
        });
    }, delay);
  }

  public setupIntersectionObserver(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const widgetId = entry.target.getAttribute('data-widget-id');
          if (widgetId) {
            if (entry.isIntersecting) {
              this.widgetsInView.add(widgetId);
            } else {
              this.widgetsInView.delete(widgetId);
            }
          }
        });
      },
      { threshold: 0.1 }
    );
  }

  public handleWindowResize(): void {
    // Update window width
    this.innerWidth = window.innerWidth;
    this.checkViewportWidth();
    
    // Clear any existing timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    // Debounce the resize operation
    this.resizeTimeout = setTimeout(() => {
      // Skip if the component is destroyed
      if (this.isDestroyed) return;
      
      
      // Trigger gridster resize if the API is available
      if (this.options && this.options['api'] && typeof this.options['api'].resize === 'function') {
        try {
          this.options['api'].resize();
        } catch (error) {
          console.error('[DASHBOARD-COMPONENT] Error during gridster resize', error);
        }
      }
      
      // Force change detection
      this.cdr.detectChanges();
    }, 200);
  }

  public checkViewportWidth(): void {
    this.isDesktopMode = this.innerWidth >= this.targetBreakpoint;
  }

  public initializeEmptyDataForWidget(widget: WidgetData): void {
    if (!widget.data) widget.data = {};
    if (widget.type === 'userProgress') {
      widget.data.users = widget.data.users || [];
      widget.data.totalCount = widget.data.totalCount || 0;
    } else if (widget.type === 'yearComparison') {
      widget.data.items = widget.data.items || [];
      widget.data.currentYear = widget.data.currentYear || [];
      widget.data.previousYear = widget.data.previousYear || [];
    } else if (widget.type === 'status') {
      widget.data.status = widget.data.status || {};
      widget.data.percentComplete = widget.data.percentComplete || 0;
    }
    widget.isLoading = false;
  }

  public handleResize(entries: ResizeObserverEntry[]): void {
    if (this.isDestroyed) return;
    
    entries.forEach(entry => {
      const chartId = entry.target.getAttribute('data-chart-id');
      if (chartId) {
        if (this.resizeTimeouts.has(chartId)) {
          clearTimeout(this.resizeTimeouts.get(chartId));
        }
        this.resizeTimeouts.set(chartId, setTimeout(() => {
          if (this.initializedCharts.has(chartId) && !this.destroyedCharts.has(chartId)) {
            this.chartService.resizeChart(chartId);
          }
        }, 200));
      }
    });
  }

  public observeChartContainers(): void {
    if (!this.intersectionObserver) return;
    this.intersectionObserver.disconnect();
    
    this.chartCanvases?.forEach((elRef: ElementRef | null) => {
      if (elRef?.nativeElement && this.intersectionObserver) {
        this.intersectionObserver.observe(elRef.nativeElement);
      }
    });
    this.statusChartCanvases?.forEach((elRef: ElementRef | null) => {
      if (elRef?.nativeElement && this.intersectionObserver) {
        this.intersectionObserver.observe(elRef.nativeElement);
      }
    });
  }

  public debugUserProgressData(): void {
    
    // Check if we have any dashboard widgets
    if (!this.dashboard || this.dashboard.length === 0) {
      return;
    }
    
    // Find any userProgress widgets
    const userProgressWidgets = this.dashboard.filter(w => w.type === 'userProgress');
    
    if (userProgressWidgets.length === 0) {
      return;
    }
    
    // Get current timestamp for tracking retry attempts
    const now = Date.now();
    
    // Log data for each widget
    userProgressWidgets.forEach((widget, index) => {
      
      // Initialize retry tracking properties if they don't exist
      if (widget.retryCount === undefined) this.initRetryTracking(widget);
      
      if (widget.data) {
        
        // Log first user if available
        if (widget.data.users && widget.data.users.length > 0) {
          
          // Reset retry count if data exists
          this.initRetryTracking(widget);
        } else {
          
          // If widget is not currently loading and has no data, consider a retry with limits
          const retryCount = widget.retryCount || 0;
          const lastRetryTime = widget.lastRetryTime || 0;
          
          if (!widget.isLoading && retryCount < 3 && (now - lastRetryTime) > 10000) {
            widget.isLoading = true;
            widget.retryCount = (retryCount || 0) + 1;
            widget.lastRetryTime = now;
            this.loadWidgetData(widget);
          } else if (retryCount >= 3) {
          }
        }
      } else {
        
        // Initialize data if missing
        widget.data = { users: [], totalCount: 0, filters: {} };
        
        // If widget is not currently loading, consider a retry with limits
        const retryCount = widget.retryCount || 0;
        const lastRetryTime = widget.lastRetryTime || 0;
        
        if (!widget.isLoading && retryCount < 3 && (now - lastRetryTime) > 10000) {
          widget.isLoading = true;
          widget.retryCount = (retryCount || 0) + 1;
          widget.lastRetryTime = now;
          this.loadWidgetData(widget);
        } else if (retryCount >= 3) {
        }
      }
      
      // Log widget settings
    });
    
    // Initialize retry tracking for all widgets
    this.dashboard.forEach(widget => {
      if (widget.retryCount === undefined) this.initRetryTracking(widget);
    });
    
    // Trigger a reload of widgets that don't have data (with retry limits)
    const widgetsWithoutData = userProgressWidgets.filter(w => {
      // Initialize retry properties if needed
      if (w.retryCount === undefined) this.initRetryTracking(w);
      
      return (!w.data || !w.data.users || w.data.users.length === 0) && 
        !w.isLoading && 
        (w.retryCount || 0) < 3 &&
        (now - (w.lastRetryTime || 0)) > 10000;
    });
    
    if (widgetsWithoutData.length > 0) {
      
      widgetsWithoutData.forEach(widget => {
        const retryCount = widget.retryCount || 0;
        widget.isLoading = true;
        widget.retryCount = retryCount + 1;
        widget.lastRetryTime = now;
        this.loadWidgetData(widget);
      });
    }
    
    // Trigger change detection to update UI
    this.cdr.detectChanges();
  }

  // --- MORE RE-ADDED METHODS TO FIX LINTER ERRORS ---

  public onGridsterInitialized(gridsterInstance: Gridster): void {
    // Replace the no-op stub so layout refresh can call the real Gridster API.
    if (this.options && gridsterInstance) {
      this.options['api'] = gridsterInstance;
    }
    this.lastLaidOutWidth = 0;
    this.lastLaidOutWidgetCount = -1;

    this.syncGridsterOptionsWithEditMode();
    this.scheduleGridsterLayout();

    this.cdr.markForCheck();
  }

  public onItemChange(item: GridsterItemConfig): void {
    this.syncGridsterItemToWidget(item);
  }

  public onItemResize(item: GridsterItemConfig): void {
    this.syncGridsterItemToWidget(item);
  }

  private syncGridsterItemToWidget(item: GridsterItemConfig): void {
    if (!item) {
      return;
    }

    const itemId = item['id'];
    if (!itemId) {
      return;
    }

    const widget = this.dashboard.find(
      (w) => w.id === itemId || w.position?.['id'] === itemId
    );
    if (!widget) {
      return;
    }

    const x = item.x ?? widget.x ?? widget.position?.x ?? 0;
    const y = item.y ?? widget.y ?? widget.position?.y ?? 0;
    const cols = item.cols ?? widget.position?.cols ?? widget.cols ?? 6;
    const rows = item.rows ?? widget.position?.rows ?? widget.rows ?? 4;

    widget.x = x;
    widget.y = y;
    widget.cols = cols;
    widget.rows = rows;

    if (!widget.position) {
      widget.position = { id: widget.id, x, y, cols, rows };
    } else {
      widget.position = { ...widget.position, id: widget.id, x, y, cols, rows };
    }

    this.cdr.markForCheck();
  }

  private syncAllWidgetPositionsFromGrid(): void {
    this.dashboard.forEach((widget) => this.syncGridsterItemToWidget(this.getGridsterItem(widget)));
  }

  public initializeAllCharts(): void {
    // Implementation needed
  }

  public storePastLayout(): void {
    this.pastLayouts.push(this.dashboard.map(widget => ({
      id: widget.id,
      x: widget.x || widget.position?.x || 0,
      y: widget.y || widget.position?.y || 0,
      cols: widget.cols || widget.position?.cols || 2,
      rows: widget.rows || widget.position?.rows || 2
    } as GridsterItemConfig)));
  }

  public saveDashboard(): void {
    if (!this.loadedDashboard?.id) {
      this.snackBar.open('Intet dashboard indlæst at gemme', 'Luk', { duration: 3000 });
      return;
    }

    if (!this.dashboard?.length) {
      this.snackBar.open('Dashboardet har ingen widgets at gemme', 'Luk', { duration: 3000 });
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();

    this.syncAllWidgetPositionsFromGrid();
    const widgets = this.dashboardService.widgetsToSavePayload(this.dashboard);
    const dashboardId = String(this.loadedDashboard.id);
    const dashboardName = this.loadedDashboard.name;
    const payload = {
      name: this.loadedDashboard.name,
      widgets,
      templateData: { widgets },
    };

    this.dashboardService.saveUserDashboardCustomization(dashboardId, payload).subscribe({
      next: (saved) => {
        this.isSaving = false;
        if (saved) {
          this.setLoadedDashboard(saved);
          this.dashboardService.cacheCurrentDashboard(saved);
        }
        localStorage.setItem('dunaWidgets', JSON.stringify(this.dashboard));
        this.snackBar.open(`Dine tilpasninger til "${dashboardName}" er gemt`, 'Luk', { duration: 4000 });
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[DASHBOARD-COMPONENT] Error saving dashboard:', err);
        this.isSaving = false;
        this.snackBar.open('Kunne ikke gemme dashboard', 'Luk', { duration: 4000 });
        this.cdr.detectChanges();
      }
    });
  }

  public getDataSourceIdForWidgetType(type: string): string { 
    
    // Map widget types to data source IDs - this mapping is crucial!
    // Updated to match the dropdown option values in the template
    const dataSourceMap: Record<string, string> = {
      'userProgress': 'user_progress',      // Changed from 'userProgress' to 'user_progress'
      'userProgressStats': 'user_progress',  // Added missing mapping
      'locationProgress': 'location_progress', // Added mapping for location progress widget
      'simpleNumber': 'simple_number',
      'cateringSummary': 'catering_summary',
      'eventsCalendar': 'events_calendar',
      'userStatus': 'user_progress',        // Default to user_progress
      'status': 'user_progress',            // Default to user_progress 
      'upcomingExercises': 'user_progress', // Default to user_progress
      'yearComparison': 'user_progress',    // Default to user_progress
      'quickLinks': 'user_progress',        // Default to user_progress
      'numberStats': 'user_progress'        // Default to user_progress
    };
    
    // First check if we have a mapping for this type
    const dataSourceId = dataSourceMap[type] || 'user_progress'; // Default fallback
    
    return dataSourceId;
  }

  /**
   * Ensure the widget has a valid data source set before editing
   */
  private ensureWidgetHasDataSource(widget: WidgetData): void {
    if (!widget.dataSource) {
      // If no data source is set, assign a default based on widget type
      widget.dataSource = this.getDataSourceIdForWidgetType(widget.type);
    } else if (!this.isValidDataSource(widget.dataSource)) {
      // If data source is invalid, reassign a default
      const oldDataSource = widget.dataSource;
      widget.dataSource = this.getDataSourceIdForWidgetType(widget.type);
    }
  }

  /**
   * Check if a data source ID is valid (matches dropdown options)
   */
  private isValidDataSource(dataSource: string): boolean {
    const validDataSources = [
      'user_progress',
      'user_attendance',
      'user_certifications',
      'location_progress',
      'location_attendance',
      'location_certifications',
      'simple_number',
      'user_count',
      'completion_rate',
      'completed_users',
      'users_with_warnings',
      'registered_to_progress_ratio',
      'catering_summary',
      'events_calendar',
    ];
    return validDataSources.includes(dataSource);
  }

  // --- BASIC TEMPLATE METHODS ---
  public getFilteredUsers(widget: WidgetData): any[] { 
    if (!widget.data?.users || !Array.isArray(widget.data.users)) {
      return [];
    }
    
    // No client-side sorting - just return data from backend
    return widget.data.users;
  }

  /**
   * TrackBy function for user items to prevent unnecessary re-renders
   * @param index Item index
   * @param user User object
   */
  public trackByUserId(index: number, user: any): string {
    return user?.id || `user-${index}`;
  }

  /**
   * TrackBy function for location items to prevent unnecessary re-renders
   * @param index Item index
   * @param location Location object
   */
  public trackByLocationId(index: number, location: any): string {
    return location?.id || `location-${index}`;
  }

  /**
   * Load more data for a widget when scrolling (infinite scroll)
   * @param widget The widget to load more data for
   */
  public loadMoreData(widget: WidgetData): void {
    // Only support infinite scroll for userProgress and locationProgress widgets
    if (widget.type !== 'userProgress' && widget.type !== 'locationProgress') {
      return;
    }
    
    widget = this.resolveDashboardWidget(widget);
    
    // Check if we should load more - use both widget state and internal map
    const isLoading = widget.isLoadingMore || widget.isLoading || this.scrollLoadingMap.get(widget.id) || false;
    if (isLoading || !widget.hasMoreData) {
      return;
    }
    
    const containerClass = widget.type === 'locationProgress' ? 'location-list' : 'user-list';
    this.storeScrollPosition(widget.id, containerClass);
    
    // Set loading flag to prevent duplicate requests
    this.scrollLoadingMap.set(widget.id, true);
    
    // Increment page number
    widget.currentPage = (widget.currentPage || 1) + 1;
    
    
    // Reload widget data (will append because currentPage > 1)
    this.loadWidgetData(widget);
  }

  /**
   * Handle scroll event for infinite scrolling with throttling
   * @param event The scroll event
   * @param widget The widget being scrolled
   */
  public onWidgetScroll(event: Event, widget: WidgetData): void {
    // Don't process scroll events while restoring scroll position
    if (this.scrollRestoringMap.get(widget.id)) {
      return;
    }
    
    const element = event.target as HTMLElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    
    // Throttle scroll events - only check every 50ms for more responsive detection
    const now = Date.now();
    const lastCheck = this.scrollThrottleMap.get(widget.id) || 0;
    if (now - lastCheck < 50) {
      return;
    }
    this.scrollThrottleMap.set(widget.id, now);
    
    // Check if user is at or near the bottom (within 400px for seamless infinite scroll)
    // Higher threshold means loading starts earlier, providing smoother experience
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    const threshold = 400; // Load more when within 400px of bottom
    
    if (distanceFromBottom <= threshold) {
      // Only trigger if not already loading
      const isLoading = widget.isLoadingMore || widget.isLoading || this.scrollLoadingMap.get(widget.id) || false;
      if (!isLoading && widget.hasMoreData) {
        this.loadMoreData(widget);
      }
    }
  }

  /**
   * Store scroll position for a widget's scrollable container
   * @param widgetId The widget ID
   * @param containerClass The class name of the scrollable container
   */
  private storeScrollPosition(widgetId: string, containerClass: string): void {
    try {
      const widgetElement = document.querySelector(`[data-widget-id="${widgetId}"]`);
      if (widgetElement) {
        const scrollContainer = widgetElement.querySelector(`.${containerClass}`) as HTMLElement;
        if (scrollContainer) {
          const scrollTop = scrollContainer.scrollTop;
          const scrollHeight = scrollContainer.scrollHeight;
          const clientHeight = scrollContainer.clientHeight;
          
          // Check if user is at the bottom (within 400px threshold - same as scroll detection)
          const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
          const isAtBottom = distanceFromBottom <= 400;
          
          // Store both scrollTop and scrollHeight to calculate relative position
          // Also store whether user was at bottom - if so, we won't restore scroll
          (scrollContainer as any).__savedScrollTop = scrollTop;
          (scrollContainer as any).__savedScrollHeight = scrollHeight;
          (scrollContainer as any).__wasAtBottom = isAtBottom;
          
        }
      }
    } catch (error) {
      console.warn(`[DASHBOARD-COMPONENT] Failed to store scroll position:`, error);
    }
  }

  /**
   * Restore scroll position for a widget's scrollable container
   * @param widgetId The widget ID
   * @param containerClass The class name of the scrollable container
   */
  private restoreScrollPosition(widgetId: string, containerClass: string): void {
    try {
      const widgetElement = document.querySelector(`[data-widget-id="${widgetId}"]`);
      if (!widgetElement) {
        return;
      }
      
      const scrollContainer = widgetElement.querySelector(`.${containerClass}`) as HTMLElement;
      if (!scrollContainer || (scrollContainer as any).__savedScrollTop === undefined) {
        return;
      }
      
      const savedScrollTop = (scrollContainer as any).__savedScrollTop;
      const savedScrollHeight = (scrollContainer as any).__savedScrollHeight;
      const wasAtBottom = (scrollContainer as any).__wasAtBottom || false;
      
      // If user was at the bottom, don't restore scroll - let them stay at bottom naturally
      if (wasAtBottom) {
        delete (scrollContainer as any).__savedScrollTop;
        delete (scrollContainer as any).__savedScrollHeight;
        delete (scrollContainer as any).__wasAtBottom;
        return;
      }
      
      // Set flag to prevent scroll events during restoration (only if not at bottom)
      this.scrollRestoringMap.set(widgetId, true);
      
      // Wait for DOM to update - check if content has actually been rendered
      const checkAndRestore = (attempt: number = 0) => {
        const currentScrollHeight = scrollContainer.scrollHeight;
        const currentClientHeight = scrollContainer.clientHeight;
        const heightDifference = currentScrollHeight - savedScrollHeight;
        
        // Check if the container has meaningful content rendered
        // If scrollHeight is very small (< 50px), content hasn't rendered yet
        const hasContent = currentScrollHeight > 50 && currentClientHeight > 0;
        
        // If content hasn't rendered yet, wait a bit more (max 20 attempts = 1000ms)
        if (!hasContent && attempt < 20) {
          setTimeout(() => checkAndRestore(attempt + 1), 50);
          return;
        }
        
        // If we still don't have content after max attempts, give up
        if (!hasContent) {
          console.error(`[DASHBOARD-COMPONENT] Failed to detect rendered content for widget ${widgetId} after ${attempt} attempts. ScrollHeight: ${currentScrollHeight}px`);
          this.scrollRestoringMap.set(widgetId, false);
          delete (scrollContainer as any).__savedScrollTop;
          delete (scrollContainer as any).__savedScrollHeight;
          delete (scrollContainer as any).__wasAtBottom;
          return;
        }
        
        // Restore scroll position - keep the same scrollTop value since new items are appended below
        scrollContainer.scrollTop = savedScrollTop;
        
        
        // Verify and retry if needed
        setTimeout(() => {
          const actualScrollTop = scrollContainer.scrollTop;
          if (Math.abs(actualScrollTop - savedScrollTop) > 1) {
            console.warn(`[DASHBOARD-COMPONENT] Scroll position mismatch! Expected ${savedScrollTop}px, got ${actualScrollTop}px. Retrying...`);
            scrollContainer.scrollTop = savedScrollTop;
            
            // Final verification
            setTimeout(() => {
              const finalScrollTop = scrollContainer.scrollTop;
              if (Math.abs(finalScrollTop - savedScrollTop) > 1) {
                console.error(`[DASHBOARD-COMPONENT] Failed to restore scroll after retry. Final position: ${finalScrollTop}px`);
              } else {
              }
            }, 50);
          }
        }, 50);
        
        // Clean up stored values
        delete (scrollContainer as any).__savedScrollTop;
        delete (scrollContainer as any).__savedScrollHeight;
        delete (scrollContainer as any).__wasAtBottom;
        
        // Clear the restoration flag after DOM has settled (reduced from 300ms to 100ms)
        setTimeout(() => {
          this.scrollRestoringMap.set(widgetId, false);
        }, 100);
      };
      
      // Start checking after a short delay to allow DOM to start updating
      setTimeout(() => {
        requestAnimationFrame(() => {
          checkAndRestore();
        });
      }, 10);
    } catch (error) {
      console.warn(`[DASHBOARD-COMPONENT] Failed to restore scroll position:`, error);
      this.scrollRestoringMap.set(widgetId, false);
    }
  }

  public toggleEditMode(): void { 
    this.editMode = !this.editMode;
    
    // Update gridster options based on edit mode
    if (this.options) {
      this.options.draggable = { enabled: this.editMode };
      this.options.resizable = { enabled: this.editMode };
      
      // Force gridster to update its options
      if (this.options['api']?.optionsChanged) {
        this.options['api'].optionsChanged();
      }
      
      // Force change detection
      this.cdr.detectChanges();
    }
  }

  public openAddWidgetDialog(): void {
    if (!this.dialog) return;
    
    
    const dialogRef = this.dialog.open(AddDashboardItemDialogComponent, {
      width: '800px',
      data: {
        title: 'Tilføj Widget',
        widgetTypes: this.availableWidgetDefinitions
          .filter(def => def.visible === true) // Only show explicitly visible widgets
          .map(def => ({
            key: def.type,
            name: def.title,
            description: def.description,
            icon: def.icon
          }))
      }
    });

    dialogRef.afterClosed().subscribe((widgetType: string) => {
      if (widgetType) {
        const widgetConfig = this.widgetAdapter.getWidgetConfig(widgetType);
        
        // Create default filters based on widget type
        const defaultFilters = widgetType === 'userProgress' || widgetType === 'userProgressStats' ? {
          group: 'all',
          sortBy: 'registeredPercentage',
          sortDirection: 'desc'
        } : widgetType === 'cateringSummary' ? {
          days: CATERING_SUMMARY_DEFAULT_DAYS,
          catering_group: null
        } : widgetType === 'eventsCalendar' ? {
          scheduleView: 'Month',
          selectedDateIso: new Date().toISOString(),
          hideSubEvents: true,
          hideFullEvents: false,
          hideParticipatedEvents: false,
          showGroupCalendarEvents: false,
          eventTypes: ['education', 'course', 'activity', 'exercise'],
          organizationIds: [],
          educationalPathIds: [],
          moduleIds: [],
          locationGuids: [],
        } : {};

        // Create new widget
        const newWidget: WidgetData = {
          id: crypto.randomUUID(),
          type: widgetType,
          title: widgetConfig.title,
          dataSource: this.getDataSourceIdForWidgetType(widgetType),
          position: {
            x: 0,
            y: 0,
            cols: widgetConfig.defaultSize?.cols || 6,
            rows: widgetConfig.defaultSize?.rows || 4,
            id: crypto.randomUUID()
          },
          cols: widgetConfig.defaultSize?.cols || 6,
          rows: widgetConfig.defaultSize?.rows || 4,
          defaultFilters: defaultFilters,
          settings: {
            category: widgetType,
            filters: { ...defaultFilters },
            fields: widgetConfig.defaultFields || [],
            displayType: widgetConfig.defaultDisplayType || 'table',
            // Add default table settings for table widgets
            ...(widgetConfig.defaultDisplayType === 'table' || !widgetConfig.defaultDisplayType ? {
              pageable: true,
              pageSize: 10,
              sortable: true,
              searchable: true,
              rowHover: true
            } : {})
          },
          isLoading: false,
          data: widgetType === 'userProgress' || widgetType === 'userProgressStats' ?
            { users: [], totalCount: 0, filters: {} } :
            widgetType === 'locationProgress' ?
            { locations: [], totalCount: 0, filters: {} } :
            widgetType === 'eventsCalendar' ?
            { events: [] } :
            { items: [] },
          eventsCalendarReloadToken: widgetType === 'eventsCalendar' ? 0 : undefined,
        };

        // Add widget to dashboard
        this.dashboard.push(newWidget);
        
        // Force change detection to render the widget immediately
        this.cdr.detectChanges();

        // Use setTimeout to ensure the widget is fully rendered before loading data
        // This fixes the visibility bug where widgets don't appear until other widgets are moved
        setTimeout(() => {
          if (!this.isDestroyed) {
            // Load widget data after the widget is properly rendered
            this.loadWidgetData(newWidget);
            
            // Additional change detection after a short delay to ensure UI updates
            setTimeout(() => {
              if (!this.isDestroyed) {
                this.cdr.detectChanges();
              }
            }, 100);
          }
        }, 50);
      }
    });
  }

  public editWidget(id: string): void {
    const widget = this.dashboard.find(w => w.id === id);
    if (widget) {
      // Ensure widget has a valid data source
      this.ensureWidgetHasDataSource(widget);
      
      // Ensure widget has settings initialized
      if (!widget.settings) {
        widget.settings = {
          category: widget.type,
          filters: {},
          fields: [],
          displayType: 'table',
          // Add default table settings
          pageable: true,
          pageSize: 15,
          sortable: true,
          searchable: true,
          rowHover: true
        };
      }
      
      // Ensure table settings exist for table widgets
      if (widget.settings && widget.settings.displayType === 'table') {
        if (widget.settings.pageable === undefined) widget.settings.pageable = true;
        if (widget.settings.pageSize === undefined) widget.settings.pageSize = 15;
        if (widget.settings.sortable === undefined) widget.settings.sortable = true;
        if (widget.settings.searchable === undefined) widget.settings.searchable = true;
        if (widget.settings.rowHover === undefined) widget.settings.rowHover = true;
      }
      
      // Ensure settings has filters object
      if (!widget.settings.filters) {
        widget.settings.filters = {};
      }
      
      
      this.selectedWidget = widget;
      this.isSettingsOpen = true;
      if (
        (this.activeSettingsTab === 'filters' && !this.hasSettingsFiltersTab(widget.type)) ||
        (this.activeSettingsTab === 'display' && !this.hasSettingsDisplayTab(widget.type))
      ) {
        this.activeSettingsTab = 'general';
      }
      this.closeFilterDialog();
      this.cdr.detectChanges();
      this.openSettingsOverlay();
    }
  }

  private openSettingsOverlay(): void {
    if (!this.settingsDialogTemplate || this.settingsOverlayRef) {
      return;
    }
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();
    this.settingsOverlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'dashboard-settings-backdrop',
      positionStrategy,
      panelClass: 'dashboard-widget-settings-panel',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width: 'min(520px, 94vw)',
      maxHeight: 'min(88vh, 720px)',
    });
    this.settingsOverlayRef.attach(
      new TemplatePortal(this.settingsDialogTemplate, this.viewContainerRef)
    );
    this.settingsOverlayRef.backdropClick().subscribe(() => this.closeSettings());
  }

  public removeWidget(id: string): void {
    const index = this.dashboard.findIndex(w => w.id === id);
    if (index !== -1) {
      this.dashboard.splice(index, 1);
      this.cdr.detectChanges();
    }
  }

  public hasFilteringSupport(type: string): boolean {
    return ['userProgress', 'locationProgress', 'userProgressStats', 'status', 'yearComparison', 'simpleNumber', 'cateringSummary', 'eventsCalendar'].includes(type);
  }

  /** Filtrering i indstillinger (datakilde/metrik) — ikke sidepanel-filtre alene */
  public hasSettingsFiltersTab(type: string): boolean {
    return [
      'userProgress',
      'userProgressStats',
      'locationProgress',
      'reminders',
      'yearComparison',
      'quickLinks',
      'status',
      'upcomingExercises',
      'numberStats',
      'simpleNumber',
    ].includes(type);
  }

  public hasDisplayTypeOptions(type: string): boolean {
    return [
      'upcomingExercises',
      'userProgress',
      'locationProgress',
      'userProgressStats',
      'reminders',
      'status',
      'yearComparison',
      'quickLinks',
      'numberStats',
    ].includes(type);
  }

  public hasDisplayFieldSettings(type: string): boolean {
    return ['status', 'upcomingExercises', 'numberStats', 'locationProgress', 'userProgress'].includes(
      type
    );
  }

  public hasSettingsDisplayTab(type: string): boolean {
    return this.hasDisplayTypeOptions(type) || this.hasDisplayFieldSettings(type);
  }

  public hasActiveFilters(widget: WidgetData): boolean {
    if (!widget?.settings?.filters) return false;
    const filters = widget.settings.filters;
    return Object.keys(filters).some(key => {
      const value = filters[key];
      if (value === undefined || value === null || value === '' || value === 'all') {
        return false;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return true;
    });
  }

  
  public openLayoutSelector(): void {
    this.isLayoutSelectorOpen = true;
  }

  public handleUndo(): void {
    if (this.pastLayouts.length > 1) {
      const currentLayout = this.pastLayouts.pop();
      if (currentLayout) {
        this.futureLayouts.push(currentLayout);
        const previousLayout = this.pastLayouts[this.pastLayouts.length - 1];
        if (previousLayout) {
          // Cast the layout to WidgetData[] to satisfy type requirements
          this.dashboard = previousLayout.map(item => ({
            ...item,
            id: item['id'] || '',
            type: (item as any)['type'] || 'unknown',
            title: (item as any)['title'] || '',
            x: item['x'] || 0,
            y: item['y'] || 0,
            cols: item['cols'] || 2,
            rows: item['rows'] || 2
          }));
          this.cdr.detectChanges();
        }
      }
    }
  }

  public handleRedo(): void {
    const nextLayout = this.futureLayouts.pop();
    if (nextLayout) {
      this.pastLayouts.push(nextLayout);
      // Cast the layout to WidgetData[] to satisfy type requirements
      this.dashboard = nextLayout.map(item => ({
        ...item,
        id: item['id'] || '',
        type: (item as any)['type'] || 'unknown',
        title: (item as any)['title'] || '',
        x: item['x'] || 0,
        y: item['y'] || 0,
        cols: item['cols'] || 2,
        rows: item['rows'] || 2
      }));
      this.cdr.detectChanges();
    }
  }

  /**
   * Save current dashboard layout as a custom template
   * This creates a personalized dashboard that won't be affected by template updates
   */
  public setActiveSettingsTab(tab: string): void {
    this.activeSettingsTab = tab;
    this.cdr.detectChanges();
  }

  public initializeYearComparisonChart(widget: WidgetData): void {
    if (!widget.data?.items?.length) return;
    const chartId = `chart-${widget.id}`;
    // Use a safe no-op for now since we don't know the exact ChartService method
  }

  public initializeStatusChart(widget: WidgetData): void {
    if (!widget.data?.status) return;
    const chartId = `status-chart-${widget.id}`;
    // Use a safe no-op for now since we don't know the exact ChartService method
  }

  private disposeSettingsOverlay(clearSelection = true): void {
    if (this.settingsOverlayRef) {
      this.settingsOverlayRef.dispose();
      this.settingsOverlayRef = null;
    }
    this.isSettingsOpen = false;
    if (clearSelection) {
      this.selectedWidget = null;
    }
    this.cdr.detectChanges();
  }

  public closeSettings(): void {
    this.disposeSettingsOverlay(true);
  }

  private syncGridsterOptionsWithEditMode(): void {
    if (!this.options) return;

    const layoutEditable = this.viewMode === 'default' && this.editMode;
    this.options.draggable = { enabled: layoutEditable };
    this.options.resizable = { enabled: layoutEditable };
    
    if (this.options['api']?.optionsChanged) {
      this.options['api'].optionsChanged();
    }

    this.cdr.markForCheck();
  }

  public forceEditModeOn(): void {
    this.editMode = true;
    
    // Force update of gridster options
    if (this.options) {
      this.options.draggable = { 
        enabled: true,
        ignoreContent: false,
        dragHandleClass: 'drag-handle',
        dropOverItems: true
      };
      this.options.resizable = { enabled: true };
      
      if (this.options['api']?.optionsChanged) {
        this.options['api'].optionsChanged();
      }
    }
    
    this.cdr.detectChanges();
    
  }

  /**
   * Create default dashboard immediately without waiting for API
   */
  private createDefaultDashboardImmediate() {
    
    // Create unique IDs for widgets
    const userProgressWidgetId = `userProgress-${Date.now()}-1`;
    
    // Create basic widget layout
    const widgetPosition: GridsterItemConfig = {
      x: 0,
      y: 0,
      cols: 6, // 6 columns wide - half the width
      rows: 4, // 4 rows tall
      id: userProgressWidgetId
    };
    
    // Create widgets with proper structure that matches WidgetData interface
    const widgets = [
      {
        id: userProgressWidgetId,
        type: 'userProgress',
        title: 'Bruger fremgang - Oversigt',
        dataSource: 'user_progress',  // Changed from 'userProgress' to 'user_progress'
        position: widgetPosition,
        x: 0,
        y: 0,
        cols: 6, // 6 columns wide - half the width
        rows: 4, // 4 rows tall
        defaultFilters: { 
          group: 'all',
          sortBy: 'registeredPercentage',
          sortDirection: 'desc' 
        },
        // IMPORTANT: Initialize settings properly with the same filters as defaultFilters
        settings: {
          category: 'userProgress',
          filters: { 
            group: 'all',
            sortBy: 'registeredPercentage',
            sortDirection: 'desc' 
          },
          fields: ['name', 'path', 'registeredPercentage', 'percentComplete', 'station'],
          displayType: 'table'
        },
        data: { users: [] }, // Initialize with empty data structure to prevent UI errors
        isLoading: false // Start with not loading to allow the loadWidgetData to run
      }
    ] as WidgetData[]; // Add type assertion here
    
    // Store in static storage for persistence
    DunaDashboardComponent.dashboardData = [...widgets];
    DunaDashboardComponent.widgetDataLoaded = true;
    
    this.dashboard = widgets;
    this.initializing = false;
    
    // Force change detection
    this.cdr.detectChanges();
    
    // Load widget data with a delay
    setTimeout(() => {
      if (!this.isDestroyed) {
        this.loadWidgetsWithDelay();
      }
    }, 300);
    
    // Save the dashboard configuration to localStorage only
    setTimeout(() => {
      // Skip if destroyed
      if (this.isDestroyed) {
        return;
      }
      
      // Create a dashboard config object
      const dashboardConfig: DashboardConfigDTO = {
        id: `default-${Date.now()}`,
        name: 'Default Dashboard',
        description: 'Default dashboard with user progress widgets',
        createdAt: new Date(),
        updatedAt: new Date(),
        isDefault: true,
        widgets: this.dashboard.map(widget => {
          // Ensure position is properly handled
          const position = widget.position || { x: 0, y: 0, cols: 12, rows: 6 };
          
          return {
            id: widget.id,
            widgetType: widget.type,
            title: widget.title,
            position: {
              x: position.x,
              y: position.y,
              cols: widget.cols || 12, 
              rows: widget.rows || 6
            },
            dataSource: widget.dataSource || 'default',
            defaultFilters: widget.defaultFilters || {}
          };
        })
      };
      
      // Save to localStorage only, not to API
      try {
        localStorage.setItem('dunaWidgets', JSON.stringify(this.dashboard));
      } catch(e) {
        console.error('[DASHBOARD-COMPONENT] Error saving to localStorage:', e);
      }
      
      // Mark stable initialization as complete after dashboard saved
      DunaDashboardComponent.stableInitComplete = true;
      
      // Mark initial layout as saved
      this.initialLayoutSaved = true;
    }, 1000);
  }

  // Add missing setup methods
  private setupResizeListeners() {
    // Add a resize listener for responsive design
    this.resizeListener = this.handleWindowResize.bind(this);
    window.addEventListener('resize', this.resizeListener);
    
    // Check viewport width initially
    this.checkViewportWidth();
  }
  
  private setupWidgetDefinitions() {
    // Define widget definitions with proper configurations
    this.availableWidgetDefinitions = [
      {
        type: 'userProgress',
        title: 'Bruger Fremgang',
        description: 'Viser bruger fremgang og status',
        icon: 'person',
        defaultSize: { cols: 6, rows: 4 },
        defaultDisplayType: 'table',
        defaultFields: ['name', 'path', 'registeredPercentage', 'percentComplete', 'station'],
        visible: true // Show in add widget dialog
      },
      {
        type: 'locationProgress',
        title: 'Lokation Fremgang',
        description: 'Viser fremgang for alle lokationer',
        icon: 'location_on',
        defaultSize: { cols: 6, rows: 4 },
        defaultDisplayType: 'table',
        defaultFields: ['name', 'userCount', 'averageProgress', 'completionRate'],
        visible: true // Show in add widget dialog
      },
      {
        type: 'userProgressStats',
        title: 'Bruger Statistik',
        description: 'Viser detaljeret bruger statistik og fremgang',
        icon: 'bar_chart',
        defaultSize: { cols: 6, rows: 4 },
        defaultDisplayType: 'chart',
        defaultFields: ['name', 'path', 'registeredPercentage', 'percentComplete', 'station', 'trend', 'lastActivity'],
        visible: true // Show in add widget dialog
      },
      {
        type: 'simpleNumber',
        title: 'Simpelt Tal',
        description: 'Viser et enkelt tal med titel',
        icon: 'looks_one',
        defaultSize: { cols: 3, rows: 2 },
        defaultDisplayType: 'number',
        defaultFields: ['value', 'title'],
        visible: true // Show in add widget dialog
      },
      {
        type: 'status',
        title: 'Status Oversigt',
        description: 'Viser overordnet status information',
        icon: 'pie_chart',
        defaultSize: { cols: 4, rows: 4 },
        defaultDisplayType: 'chart',
        defaultFields: ['status', 'count'],
        visible: false // Hide from add widget dialog
      },
      {
        type: 'quickLinks',
        title: 'Hurtig Links',
        description: 'Viser hurtig links til andre systemer',
        icon: 'link',
        defaultSize: { cols: 4, rows: 3 },
        defaultDisplayType: 'buttons',
        defaultFields: ['title', 'url', 'icon'],
        visible: false // Hide from add widget dialog
      }
    ];
    
    // Extract widget types for backwards compatibility
    this.availableWidgetTypes = this.availableWidgetDefinitions.map(def => def.type);
    
  }

  // Add a public method for testing user progress data loading from the UI
  public testLoadUserProgressData(): void {
    
    // Find the userProgress widgets
    const userProgressWidgets = this.dashboard.filter(w => w.type === 'userProgress');
    
    if (userProgressWidgets.length === 0) {
      return;
    }
    
    
    // For each widget, try loading data directly
    userProgressWidgets.forEach((widget, index) => {
      
      // Get filters from settings or defaultFilters
      const filters = widget.settings?.filters || widget.defaultFilters || {};
      
      // Mark as loading
      widget.isLoading = true;
      this.cdr.detectChanges();
      
      // Call getUserProgressData directly
      const listRequest = createListRequest({
        page: 1,
        pageSize: 50,
        sortBy: filters['sortBy'],
        sortDir: filters['sortDirection'] as 'asc' | 'desc',
        filters: filters,
        search: filters['search'],
        includeFilters: true
      });
      
      getUserProgressData(this.http, listRequest)
        .pipe(
          take(1),
          timeout(10000),
          catchError((err: HttpErrorResponse) => {
            console.error(`❌ [DASHBOARD-COMPONENT] TEST: Error loading data:`, err);
            return of({
              data: [],
              meta: {
                page: 1,
                pageSize: 50,
                totalItems: 0,
                totalPages: 0
              }
            } as ListResponseDTO);
          })
        )
        .subscribe({
          next: (response: ListResponseDTO) => {
            
            // Update widget data
            widget.data = {
              users: response.data || [],
              totalCount: response.meta?.totalItems || 0,
              filters: response.filters || {}
            };
            
            // Create backup of original data to preserve filter options (only if not already backed up)
            if (!widget.originalData && response.data && response.data.length > 0) {
              widget.originalData = { ...widget.data };
            }
            
            widget.isLoading = false;
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error(`❌ [DASHBOARD-COMPONENT] TEST: Error in subscription:`, err);
            widget.isLoading = false;
            this.cdr.detectChanges();
          },
          complete: () => {
            widget.isLoading = false;
            this.cdr.detectChanges();
          }
        });
    });
  }

  /**
   * Force reload of all widgets, regardless of their loading state
   * This is a more aggressive approach than the test function
   */
  public forceReloadAllWidgets(): void {
    
    if (!this.dashboard || this.dashboard.length === 0) {
      console.warn('[DASHBOARD-COMPONENT] No widgets to reload');
      return;
    }
    
    // Reset all widgets to not loading first
    this.dashboard.forEach(widget => {
      widget.isLoading = false;
      
      // Ensure settings are properly initialized
      if (!widget.settings && widget.defaultFilters) {
        widget.settings = {
          category: widget.type,
          filters: { ...widget.defaultFilters },
          fields: ['name', 'path', 'registeredPercentage', 'percentComplete', 'station'],
          displayType: 'table'
        };
      } else if (!widget.settings) {
        widget.settings = {
          category: widget.type,
          filters: {},
          fields: ['name', 'path', 'registeredPercentage', 'percentComplete', 'station'],
          displayType: 'table'
        };
      }
    });
    
    // Force change detection
    this.cdr.detectChanges();
    
    // Then force load each widget one by one with a small delay between them
    this.dashboard.forEach((widget, index) => {
      setTimeout(() => {
        if (this.isDestroyed) return;
        
        this.loadWidgetData(widget);
      }, index * 300); // Slightly longer delay to ensure sequential loading
    });
  }

  /**
   * Force reload location progress widgets specifically with real API data
   */
  public forceReloadLocationProgressWidgets(): void {
    
    const locationWidgets = this.dashboard.filter(widget => widget.type === 'locationProgress');
    
    if (locationWidgets.length === 0) {
      console.warn('[DASHBOARD-COMPONENT] No location progress widgets found');
      return;
    }
    
    
    locationWidgets.forEach((widget, index) => {
      setTimeout(() => {
        if (this.isDestroyed) return;
        
        
        // Set loading state
        widget.isLoading = true;
        this.cdr.detectChanges();
        
        // Get filters from widget settings
        const filters = widget.settings?.filters || {};
        
        // Create list request for location progress
        const listRequest: ListRequestDTO = createListRequest({
          page: 1,
          pageSize: 100,
          sortBy: filters['sortBy'],
          sortDir: filters['sortDirection'] as 'asc' | 'desc',
          filters: filters,
          search: filters['search'],
          includeFilters: true
        });
        
        // Use the force reload function to ensure real API data
        forceReloadLocationProgressData(this.componentDataSourceService, listRequest).subscribe({
          next: (response: ListResponseDTO) => {
            
            // Update widget data
            widget.data = {
              locations: response.data || [],
              totalCount: response.meta?.totalItems || 0,
              filters: filters
            };
            
            // Set loading state
            widget.isLoading = false;
            
            // Force change detection
            this.cdr.detectChanges();
          },
          error: (error: any) => {
            console.error(`🏢❌ [DASHBOARD-COMPONENT] Error force reloading location progress data:`, error);
            
            // Set loading state
            widget.isLoading = false;
            
            // Force change detection
            this.cdr.detectChanges();
          }
        });
      }, index * 500); // Delay between widgets
    });
  }

  onEventsCalendarFiltersChange(
    widget: WidgetData,
    filters: Record<string, unknown>
  ): void {
    const w = this.resolveDashboardWidget(widget);
    if (!w.settings) return;
    w.settings.filters = { ...filters };
    w.eventsCalendarReloadToken = (w.eventsCalendarReloadToken || 0) + 1;
    this.cdr.detectChanges();
  }

  onEventsCalendarFilterPanelChange(filters: Record<string, unknown>): void {
    if (!this.selectedWidget?.settings) return;
    this.selectedWidget.settings.filters = { ...filters };
    this.selectedWidget.eventsCalendarReloadToken =
      (this.selectedWidget.eventsCalendarReloadToken || 0) + 1;
    this.cdr.detectChanges();
  }

  public applyFilterAndLoad(widget: any, key: string, value: any): void {
    widget = this.resolveDashboardWidget(widget);
    if (!widget.settings) return;
    widget.settings.filters = widget.settings.filters || {};
    
    // Special handling for simple number widget dataSource changes
    if (widget.type === 'simpleNumber' && key === 'dataSource') {
      widget.dataSource = value;
    } else {
      widget.settings.filters[key] = this.normalizeFilterValue(key, value);
      if (key === 'station') {
        delete widget.settings.filters['stations'];
      }
      if (key === 'stations') {
        delete widget.settings.filters['station'];
      }
    }
    
    // Reset pagination when filters change
    widget.currentPage = 1;
    widget.hasMoreData = true;
    this.updateProgressListLoadingMore(widget, false);
    
    // Immediately load widget data to update with the new filter
    this.loadWidgetData(widget);
    
    // Force change detection to update the UI immediately
    this.cdr.detectChanges();
    
    // If this is the selected widget in the filter dialog, refresh the filter indicators
    if (this.selectedWidget && this.selectedWidget.id === widget.id) {
      this.selectedWidget = widget; // Update the selected widget reference
    }
  }

  loadShareLinkDashboard(): void {
    const guid = this.shareLinkGuid?.trim();
    if (!this.options) {
      this.initializeGridsterOptions();
      this.syncGridsterOptionsWithEditMode();
    }
    if (!guid) {
      this.showEmptyState = true;
      this.loadDashboardComplete = true;
      this.cdr.detectChanges();
      return;
    }
    this.shareLinkContext.setShareLink(guid);
    this.dashboardService.getPublicShareLinkDashboard(guid).subscribe({
      next: (config) => {
        if (config?.widgets?.length) {
          this.applyDashboardFromConfig(config);
        } else {
          this.showEmptyState = true;
          this.loadDashboardComplete = true;
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.showEmptyState = true;
        this.loadDashboardComplete = true;
        this.snackBar.open('Kunne ikke indlæse delt dashboard', 'Luk', { duration: 4000 });
        this.cdr.detectChanges();
      },
    });
  }

  saveShareLinkFilters(): void {
    const guid = this.shareLinkGuid?.trim();
    if (!guid) {
      return;
    }
    this.syncAllWidgetPositionsFromGrid();
    const widgets = this.dashboardService.widgetsToSavePayload(this.dashboard);
    this.savingShareLinkFilters = true;
    this.dashboardService
      .updateDashboardShareLink(guid, { templateData: { widgets }, widgets })
      .subscribe({
        next: () => {
          this.savingShareLinkFilters = false;
          this.snackBar.open('Filtre gemt for delingslink', 'Luk', { duration: 3000 });
          this.cdr.detectChanges();
        },
        error: () => {
          this.savingShareLinkFilters = false;
          this.snackBar.open('Kunne ikke gemme filtre', 'Luk', { duration: 4000 });
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Opens the dashboard template manager in a dialog
   */
  openTemplateManager(): void {
    this.syncAllWidgetPositionsFromGrid();
    // Import the component here to use in dialog
    import('./dashboard-template-manager.component').then(({ DashboardTemplateManagerComponent }) => {
      const dialogRef = this.dialog.open(DashboardTemplateManagerComponent, {
        width: '80vw',
        height: '80vh',
        maxWidth: '1200px',
        maxHeight: '800px',
        panelClass: 'template-manager-dialog',
        data: {
          loadedDashboard: this.loadedDashboard,
          currentWidgets: this.dashboard
        }
      });

      dialogRef.afterClosed().subscribe((result?: { applied?: boolean; dashboard?: DashboardConfigDTO }) => {
        if (result?.applied && result.dashboard) {
          this.applyDashboardFromConfig(result.dashboard);
          return;
        }

        if (result?.applied) {
          this.loadDashboard(true);
        }
      });
    });
  }

  // Methods for userProgressStats widget calculations
  public getAverageProgress(widget: WidgetData): number {
    if (!widget.data?.users || widget.data.users.length === 0) return 0;
    
    const total = widget.data.users.reduce((sum: number, user: any) => {
      // Use actual progress percentage for the main donut chart
      const progress = user.percentComplete || user.progress_percentage || 0;
      return sum + progress;
    }, 0);
    
    return Math.round(total / widget.data.users.length);
  }

  public getAverageRegistered(widget: WidgetData): number {
    if (!widget.data?.users || widget.data.users.length === 0) return 0;
    
    const total = widget.data.users.reduce((sum: number, user: any) => {
      const registered = user.registeredPercentage || 0;
      return sum + registered;
    }, 0);
    
    return Math.round(total / widget.data.users.length);
  }

  public getAverageActualProgress(widget: WidgetData): number {
    if (!widget.data?.users || widget.data.users.length === 0) return 0;
    
    const total = widget.data.users.reduce((sum: number, user: any) => {
      const progress = user.percentComplete || user.progress_percentage || 0;
      return sum + progress;
    }, 0);
    
    return Math.round(total / widget.data.users.length);
  }

  public getProgressRanges(widget: WidgetData): any[] {
    if (!widget.data?.users || widget.data.users.length === 0) return [];
    
    const ranges = [
      { min: 0, max: 25, label: '0-25%', color: '#f44336', registeredCount: 0, progressCount: 0 },
      { min: 26, max: 50, label: '26-50%', color: '#ff9800', registeredCount: 0, progressCount: 0 },
      { min: 51, max: 75, label: '51-75%', color: '#2196f3', registeredCount: 0, progressCount: 0 },
      { min: 76, max: 100, label: '76-100%', color: '#4caf50', registeredCount: 0, progressCount: 0 }
    ];
    
    widget.data.users.forEach((user: any) => {
      const registeredPercentage = user.registeredPercentage || 0;
      const progressPercentage = user.percentComplete || user.progress_percentage || 0;
      
      // Count registered percentage ranges
      for (const range of ranges) {
        if (registeredPercentage >= range.min && registeredPercentage <= range.max) {
          range.registeredCount++;
          break;
        }
      }
      
      // Count progress percentage ranges
      for (const range of ranges) {
        if (progressPercentage >= range.min && progressPercentage <= range.max) {
          range.progressCount++;
          break;
        }
      }
    });
    
    const totalUsers = widget.data.users.length;
    return ranges.map(range => ({
      ...range,
      registeredPercentage: totalUsers > 0 ? Math.round((range.registeredCount / totalUsers) * 100) : 0,
      progressPercentage: totalUsers > 0 ? Math.round((range.progressCount / totalUsers) * 100) : 0,
      // Keep backward compatibility
      count: range.registeredCount,
      percentage: totalUsers > 0 ? Math.round((range.registeredCount / totalUsers) * 100) : 0
    }));
  }

  public getCompletedCount(widget: WidgetData): number {
    if (!widget.data?.users || widget.data.users.length === 0) return 0;
    
    return widget.data.users.filter((user: any) => {
      const progress = user.registeredPercentage || user.percentComplete || 0;
      return progress >= 100;
    }).length;
  }

  public getInProgressCount(widget: WidgetData): number {
    if (!widget.data?.users || widget.data.users.length === 0) return 0;
    
    return widget.data.users.filter((user: any) => {
      const progress = user.registeredPercentage || user.percentComplete || 0;
      return progress > 0 && progress < 100;
    }).length;
  }

  public getNotStartedCount(widget: WidgetData): number {
    if (!widget.data?.users || widget.data.users.length === 0) return 0;
    
    return widget.data.users.filter((user: any) => {
      const progress = user.registeredPercentage || user.percentComplete || 0;
      return progress === 0;
    }).length;
  }

  /**
   * Handle data source changes for widgets
   */
  public onDataSourceChange(widget: WidgetData): void {
    
    // Explicitly set loading state first
    widget.isLoading = true;
    
    // Clear existing data to show loading state
    widget.data = widget.type === 'userProgress' || widget.type === 'userProgressStats' 
      ? { users: [], totalCount: 0, filters: {} }
      : { items: [], totalCount: 0, filters: {} };
    
    // Force change detection to show loading state immediately
    this.cdr.detectChanges();
    
    // Use setTimeout to ensure proper change detection timing similar to widget addition
    setTimeout(() => {
      if (!this.isDestroyed) {
        // Load new data based on the selected data source
        this.loadWidgetData(widget);
        
        // Additional change detection after a short delay to ensure UI updates
        setTimeout(() => {
          if (!this.isDestroyed) {
            this.cdr.detectChanges();
          }
        }, 100);
      }
    }, 50);
    
    // Save the dashboard to persist the change
    this.saveDashboard();
  }

  /**
   * Migrate old widgets to ensure they have correct data source format
   */
  private migrateWidgetDataSources(widgets: WidgetData[]): WidgetData[] {
    return widgets.map(widget => {
      const migratedWidget = { ...widget };
      
      // Ensure widget has a data source
      this.ensureWidgetHasDataSource(migratedWidget);
      
      // Log migration if data source was changed
      if (widget.dataSource !== migratedWidget.dataSource) {
      }
      
      return migratedWidget;
    });
  }

  /**
   * Navigate to a user's profile page
   * @param user The user object containing user information
   */
  public navigateToUserProfile(user: any): void {
    if (!user || !user.id) {
      console.warn('[DASHBOARD-COMPONENT] Cannot navigate: user or user.id is undefined');
      return;
    }

    try {
      // Since we're in a dashboard context and user has clicked on a specific user,
      // navigate to the user profile page
      const userProfileUrl = `/profile/${user.id}`;
      
      
      // Use window.location to navigate (alternative to Router which might not be injected)
      // TODO: Make this open in a new tab in the future
      window.open(userProfileUrl, '_blank');
      
    } catch (error) {
      console.error('[DASHBOARD-COMPONENT] Error navigating to user profile:', error);
    }
  }

  /**
   * Navigate to location details page
   */
  public navigateToLocationDetails(location: any): void {
    if (!location || !location.id) {
      console.warn('[DASHBOARD-COMPONENT] Cannot navigate: location or location.id is undefined');
      return;
    }

    try {
      // Navigate to the location details page
      // TODO: Make this open in a new tab in the future
      const locationDetailsUrl = `/lokation/${location.id}`;
      
      
      // Use window.location to navigate
      window.open(locationDetailsUrl, '_blank');
      
    } catch (error) {
      console.error('[DASHBOARD-COMPONENT] Error navigating to location details:', error);
    }
  }

  public getFilteredLocations(widget: WidgetData): any[] {
    if (!widget.data?.locations) {
      return [];
    }

    let filteredLocations = widget.data.locations || [];

    // Filter by search text if provided
    if (this.locationSearchText && this.locationSearchText.trim()) {
      const searchText = this.locationSearchText.toLowerCase().trim();
      filteredLocations = filteredLocations.filter((location: any) => 
        location.name?.toLowerCase().includes(searchText) ||
        location.type?.toLowerCase().includes(searchText)
      );
    }

    return filteredLocations;
  }

  /** True while the first fetch or a scroll load is in progress for list widgets. */
  public isProgressListLoadMoreUiVisible(widget: WidgetData): boolean {
    return this.progressListLoadMoreUiVisible.get(widget.id) === true;
  }

  private scheduleProgressListLoadMoreUi(widgetId: string): void {
    this.cancelProgressListLoadMoreUi(widgetId);
    const timer = setTimeout(() => {
      this.progressListLoadMoreUiTimers.delete(widgetId);
      this.progressListLoadMoreUiVisible.set(widgetId, true);
      this.cdr.markForCheck();
    }, DunaDashboardComponent.PROGRESS_LIST_LOAD_MORE_UI_DELAY_MS);
    this.progressListLoadMoreUiTimers.set(widgetId, timer);
  }

  private cancelProgressListLoadMoreUi(widgetId: string): void {
    const pending = this.progressListLoadMoreUiTimers.get(widgetId);
    if (pending !== undefined) {
      clearTimeout(pending);
      this.progressListLoadMoreUiTimers.delete(widgetId);
    }
    if (this.progressListLoadMoreUiVisible.delete(widgetId)) {
      this.cdr.markForCheck();
    }
  }

  private updateProgressListLoadingMore(widget: WidgetData, loadingMore: boolean): void {
    widget.isLoadingMore = loadingMore;
    if (loadingMore) {
      this.scheduleProgressListLoadMoreUi(widget.id);
    } else {
      this.cancelProgressListLoadMoreUi(widget.id);
    }
  }

  /** True while the first fetch or a delayed scroll-load overlay should show. */
  public isProgressListLoading(widget: WidgetData): boolean {
    return (
      !!widget.isLoading ||
      widget.initialLoadComplete === false ||
      this.isProgressListLoadMoreUiVisible(widget)
    );
  }

  public isProgressListEmpty(widget: WidgetData): boolean {
    if (this.isProgressListLoading(widget)) {
      return false;
    }
    if (widget.type === 'locationProgress') {
      return !widget.data?.locations?.length;
    }
    if (widget.type === 'userProgress') {
      return !widget.data?.users?.length;
    }
    return false;
  }

  private static readonly PROGRESS_LIST_INFINITE_SCROLL_PAGE_SIZE = 10;

  private buildProgressListRequest(
    widget: WidgetData,
    filters: Record<string, unknown>,
    currentPage: number,
    pageSize: number
  ): ListRequestDTO {
    const sortDir = (filters['sortDir'] || filters['sortDirection'] || 'desc') as 'asc' | 'desc';
    const uiSortBy = filters['sortBy'] as string | undefined;
    const sortBy =
      widget.type === 'locationProgress'
        ? this.mapLocationProgressSortByForApi(uiSortBy)
        : this.mapUserProgressSortByForApi(uiSortBy);

    return createListRequest({
      page: currentPage,
      pageSize,
      sortBy,
      sortDir,
      filters,
      search: filters['search'] as string | undefined,
      includeFilters: true,
    });
  }

  private mapUserProgressSortByForApi(sortBy?: string): string | undefined {
    if (!sortBy) {
      return undefined;
    }
    const map: Record<string, string> = {
      registeredPercentage: 'completion',
      percentComplete: 'completion',
      station: 'location',
      name: 'name',
      status: 'status',
      path: 'name',
    };
    return map[sortBy] ?? sortBy;
  }

  private mapLocationProgressSortByForApi(sortBy?: string): string | undefined {
    if (!sortBy) {
      return undefined;
    }
    const map: Record<string, string> = {
      registeredPercentage: 'registered_percentage',
      percentComplete: 'progress_percentage',
      averageProgress: 'registered_percentage',
      completionRate: 'progress_percentage',
      userCount: 'firefighter_count',
      name: 'name',
    };
    return map[sortBy] ?? sortBy;
  }

  private computeProgressListHasMoreData(
    widget: WidgetData,
    response: ListResponseDTO,
    listRequest: ListRequestDTO,
    isAppending: boolean,
    lastBatchCount: number
  ): boolean {
    const pageSize = listRequest.pageSize || DunaDashboardComponent.PROGRESS_LIST_INFINITE_SCROLL_PAGE_SIZE;
    if (isAppending && lastBatchCount === 0) {
      return false;
    }
    if (lastBatchCount < pageSize) {
      return false;
    }
    const loadedCount =
      widget.type === 'locationProgress'
        ? widget.data?.locations?.length ?? 0
        : widget.data?.users?.length ?? 0;
    const metaTotal = response.meta?.totalItems;
    if (typeof metaTotal === 'number' && metaTotal > loadedCount) {
      return true;
    }
    return lastBatchCount >= pageSize;
  }

  private syncWidgetLocationFilterCache(widget: WidgetData, response: ListResponseDTO): void {
    if (widget.type !== 'userProgress' && widget.type !== 'userProgressStats') {
      return;
    }
    const fromFilters = extractLocationNamesFromListFilters(response.filters);
    if (fromFilters.length > 0) {
      this.allWidgetLocations.set(widget.id, [...fromFilters].sort());
      return;
    }
    const fromRows = [...new Set(
      (widget.data?.users || [])
        .map((u: { station?: string }) => u.station)
        .filter((s: string | undefined): s is string => typeof s === 'string' && s !== 'Unknown')
    )].sort() as string[];
    if (fromRows.length > 0) {
      this.allWidgetLocations.set(widget.id, fromRows);
    }
  }

  private getProgressListPageSize(widget: WidgetData): number {
    if (widget.type === 'userProgressStats') {
      return 1000;
    }
    if (widget.type !== 'userProgress' && widget.type !== 'locationProgress') {
      return 1000;
    }
    const tableSettings = widget.settings as { pageable?: boolean; pageSize?: number };
    if (tableSettings?.pageable === false) {
      return 1000;
    }
    return DunaDashboardComponent.PROGRESS_LIST_INFINITE_SCROLL_PAGE_SIZE;
  }

  /**
   * Get the count of data rows available for export
   * @param widget The widget to get export data count for
   * @returns Number of rows that would be exported
   */
  public getExportDataCount(widget: WidgetData): number {
    if (!widget || !widget.data) return 0;
    
    if (widget.type === 'userProgress' || widget.type === 'userProgressStats') {
      return widget.data.users?.length || 0;
    }
    
    return widget.data.items?.length || 0;
  }

  /**
   * Export widget data to the specified format
   * @param widget The widget containing the data to export
   * @param format The format to export to ('excel', 'pdf', or 'csv')
   */
  public exportWidgetData(widget: WidgetData, format: 'excel' | 'pdf' | 'csv'): void {
    if (!widget || !widget.data) {
      console.warn('[DASHBOARD-COMPONENT] Cannot export: no widget or data provided');
      return;
    }

    
    // Set exporting state
    this.isExporting = true;
    this.cdr.detectChanges();

    try {
      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
      const baseFilename = this.customExportFilename || `${widget.title.replace(/[^a-zA-Z0-9]/g, '_')}_${timestamp}`;
      
      // Get the data to export
      let dataToExport: any[] = [];
      if (widget.type === 'userProgress' || widget.type === 'userProgressStats') {
        dataToExport = widget.data.users || [];
      } else {
        dataToExport = widget.data.items || [];
      }

      if (dataToExport.length === 0) {
        alert('Ingen data at eksportere');
        this.isExporting = false;
        this.cdr.detectChanges();
        return;
      }

      // Perform export based on format
      switch (format) {
        case 'excel':
          this.exportToExcel(dataToExport, baseFilename, widget);
          break;
        case 'pdf':
          this.exportToPDF(dataToExport, baseFilename, widget);
          break;
        case 'csv':
          this.exportToCSV(dataToExport, baseFilename, widget);
          break;
        default:
          console.error('[DASHBOARD-COMPONENT] Unknown export format:', format);
      }
    } catch (error) {
      console.error('[DASHBOARD-COMPONENT] Error during export:', error);
      alert('Der opstod en fejl under eksport. Prøv igen.');
    } finally {
      // Reset exporting state
      setTimeout(() => {
        this.isExporting = false;
        this.cdr.detectChanges();
      }, 1000);
    }
  }

  /**
   * Export data to Excel format
   */
  private exportToExcel(data: any[], filename: string, widget: WidgetData): void {
    
    // Create a simple CSV format that Excel can open
    // In a real implementation, you might use a library like xlsx or exceljs
    const csvContent = this.generateCSVContent(data, widget);
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
  }

  /**
   * Export data to PDF format
   */
  private exportToPDF(data: any[], filename: string, widget: WidgetData): void {
    
    // For a basic implementation, we'll create a printable HTML version
    // In a real implementation, you might use a library like jsPDF or pdfmake
    
    let htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${widget.title}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; font-weight: bold; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .info { margin-bottom: 20px; color: #666; }
        </style>
      </head>
      <body>
    `;
    
    if (this.exportIncludeLogo) {
      htmlContent += `<h1>${widget.title}</h1>`;
    }
    
    if (this.exportIncludeFilters && this.hasActiveFilters(widget)) {
      htmlContent += `<div class="info">Aktive filtre: ${this.getActiveFiltersText(widget)}</div>`;
    }
    
    htmlContent += `<div class="info">Eksporteret: ${new Date().toLocaleString('da-DK')}</div>`;
    htmlContent += this.generateHTMLTable(data, widget);
    htmlContent += '</body></html>';
    
    // Open in new window for printing/saving as PDF
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(htmlContent);
      newWindow.document.close();
      newWindow.print();
    }
    
  }

  /**
   * Export data to CSV format
   */
  private exportToCSV(data: any[], filename: string, widget: WidgetData): void {
    
    const csvContent = this.generateCSVContent(data, widget);
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
  }

  /**
   * Generate CSV content from data
   */
  private generateCSVContent(data: any[], widget: WidgetData): string {
    if (data.length === 0) return '';
    
    // Get column headers
    const headers = this.getExportColumns(widget);
    let csvContent = '';
    
    // Add headers if requested
    if (this.exportIncludeHeaders) {
      csvContent += headers.map(h => `"${h.label}"`).join(',') + '\n';
    }
    
    // Add data rows
    data.forEach(item => {
      const row = headers.map(header => {
        const value = item[header.key] || '';
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',');
      csvContent += row + '\n';
    });
    
    return csvContent;
  }

  /**
   * Generate HTML table from data
   */
  private generateHTMLTable(data: any[], widget: WidgetData): string {
    if (data.length === 0) return '<p>Ingen data at vise</p>';
    
    const headers = this.getExportColumns(widget);
    let html = '<table>';
    
    // Add headers
    if (this.exportIncludeHeaders) {
      html += '<thead><tr>';
      headers.forEach(header => {
        html += `<th>${header.label}</th>`;
      });
      html += '</tr></thead>';
    }
    
    // Add data rows
    html += '<tbody>';
    data.forEach(item => {
      html += '<tr>';
      headers.forEach(header => {
        const value = item[header.key] || '';
        html += `<td>${String(value)}</td>`;
      });
      html += '</tr>';
    });
    html += '</tbody></table>';
    
    return html;
  }

  /**
   * Get export columns based on widget type
   */
  private getExportColumns(widget: WidgetData): {key: string, label: string}[] {
    if (widget.type === 'userProgress' || widget.type === 'userProgressStats') {
      return [
        { key: 'name', label: 'Navn' },
        { key: 'station', label: 'Station' },
        { key: 'path', label: 'Uddannelsesvej' },
        { key: 'percentComplete', label: 'Fremgang (%)' },
        { key: 'status', label: 'Status' }
      ];
    }
    
    // Default columns for other widget types
    return [
      { key: 'name', label: 'Navn' },
      { key: 'value', label: 'Værdi' },
      { key: 'status', label: 'Status' }
    ];
  }

  /**
   * Get active filters as text string
   */
  private getActiveFiltersText(widget: WidgetData): string {
    if (!widget?.settings?.filters) return '';
    
    const filters = widget.settings.filters;
    const activeFilters: string[] = [];
    
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '' && value !== 'all') {
        activeFilters.push(`${key}: ${value}`);
      }
    });
    
    return activeFilters.join(', ');
  }

  public openFilterModal(widget: WidgetData): void {
    if (!this.canOpenWidgetFilters()) {
      return;
    }

    // Ensure widget has settings initialized
    if (!widget.settings) {
      widget.settings = {
        category: widget.type,
        filters: {},
        fields: [],
        displayType: 'table'
      };
    }
    
    // Ensure settings has filters object
    if (!widget.settings.filters) {
      widget.settings.filters = {};
    }
    
    this.disposeSettingsOverlay(false);
    this.selectedWidget = widget;
    this.isFilterOpen = true;

    this.ensureFilterReferenceData(widget);

    // Pre-load all locations for this widget if not already cached
    if ((widget.type === 'userProgress' || widget.type === 'userProgressStats') &&
        !this.allWidgetLocations.has(widget.id)) {
      this.loadAllLocationsForWidget(widget);
    }
    
    this.cdr.detectChanges();

    // Render filter panel in CDK overlay container (above nav) so it is not trapped in dashboard stacking context
    if (this.filterPanelTemplate && this.filterOverlayRef === null) {
      const positionStrategy = this.overlay.position()
        .global()
        .right('0')
        .top('0')
        .bottom('0');
      this.filterOverlayRef = this.overlay.create({
        hasBackdrop: true,
        backdropClass: 'filter-backdrop',
        positionStrategy,
        width: '380px',
        maxWidth: '100vw',
        panelClass: 'filter-side-panel',
        scrollStrategy: this.overlay.scrollStrategies.noop()
      });
      this.filterOverlayRef.attach(new TemplatePortal(this.filterPanelTemplate, this.viewContainerRef));
      this.filterOverlayRef.backdropClick().subscribe(() => this.closeFilterDialog());
    }
  }

  public onLocationSearchInput(event: any): void {
    // The search filtering is handled automatically by the two-way binding 
    // and the existing getFilteredLocations method
  }

  public clearLocationSearch(): void {
    this.locationSearchText = '';
  }

  /**
   * Gets the DUNA validation status for a user
   * @param user The user object containing validation information
   * @returns The DUNA validation status
   */
  public getUserValidationStatus(user: any): DunaValidationStatus {
    
    // Check if user has validation_status field (new field)
    if (user?.validation_status) {
      return user.validation_status;
    }
    
    // Check if user has nested status.validation_status field (as seen in user-progress.data.ts)
    if (user?.status?.validation_status) {
      return user.status.validation_status;
    }
    
    // Check if user has duna_status field
    if (user?.duna_status) {
      return user.duna_status;
    }
    
    // Check if user has paths with validation status
    if (user?.paths && Array.isArray(user.paths) && user.paths.length > 0) {
      // If user has multiple paths, determine overall status
      const pathStatuses = user.paths.map((path: any) => path.validation_status || 'attention');
      
      // If any path is valid, user is valid
      if (pathStatuses.includes('valid')) {
        return 'valid';
      }
      
      // If any path needs attention, user needs attention
      if (pathStatuses.includes('attention')) {
        return 'attention';
      }
      
      // If any path has no data, user has no data
      if (pathStatuses.includes('no_data')) {
        return 'no_data';
      }
      
      // If all paths have no paths, user has no paths
      if (pathStatuses.every((status: string) => status === 'no_paths')) {
        return 'no_paths';
      }
    }
    
    // Check if user has status field that maps to validation status
    if (user?.status) {
      switch (user.status.toLowerCase()) {
        case 'ok':
        case 'valid':
        case 'complete':
          return 'valid';
        case 'warning':
        case 'attention':
        case 'incomplete':
          return 'attention';
        case 'new':
        case 'no_data':
          return 'no_data';
        case 'no_paths':
          return 'no_paths';
        default:
          return 'attention';
      }
    }
    
    // Fallback: if ok_status is still present, use it (but log a warning)
    if (user?.ok_status !== undefined) {
      console.warn('[DASHBOARD-COMPONENT] Using deprecated ok_status field for user validation status');
      const result = user.ok_status === true ? 'valid' : 'attention';
      return result;
    }
    
    // Default fallback
    return 'attention';
  }

  /**
   * Gets the DUNA validation tooltip for a user
   * @param user The user object containing validation information
   * @returns The tooltip text
   */
  public getUserValidationTooltip(user: any): string {
    const status = this.getUserValidationStatus(user);
    const lastYear = new Date().getFullYear() - 1;
    const userName = user?.name || 'Bruger';
    
    switch (status) {
      case 'valid':
        return `${userName}s uddannelsesstie(r) er gyldig baseret på deltagelse i ${lastYear}`;
      case 'attention':
        return `${userName}s uddannelsesstie(r) kræver opmærksomhed - tjek venligst deltagelse og moduler for ${lastYear}`;
      case 'no_data':
        return `Ingen data tilgængelig for ${userName} i ${lastYear} - dette er normalt for nye brugere`;
      case 'no_paths':
        return `Ingen uddannelsesveje tilgængelige for ${userName} i ${lastYear}`;
      default:
        return `${userName}s uddannelsesstie(r) kræver opmærksomhed`;
    }
  }
}